// Wire shapes + the api surface, both as zod schemas and inferred TS
// types. The api surface (`Api`) is grouped by entity — `api.products.list()`,
// not `api.listProducts()` — so each domain owns one namespace and one file
// under ./mock/ (and one impl under ./http.ts).
import { z } from "zod";
import type {
	CategoryCompliance,
	CategoryComplianceInput,
	ComplianceAdmissionInput,
	ComplianceDocument,
	ComplianceMatrixRow,
	ComplianceReport,
	ProductCompliance,
} from "@osmix/contracts/compliance";
import type {
	CatalogImportBulkPatch,
	CatalogImportCommit,
	CatalogImportFile,
	CatalogImportFromApplication,
	CatalogImportItem,
	CatalogImportItemPatch,
	CatalogImportJob,
	CatalogImportJobCreate,
	CatalogImportJobDetail,
	CatalogImportJobPatch,
	CatalogImportModels,
	CatalogImportRegenerate,
	CatalogImportTextInput,
	CrmActivity,
	CrmActivityInput,
	CrmActivityPatch,
	CrmBoardExtras,
	CrmContact,
	CrmContactInput,
	CrmContactPatch,
	CrmDuplicate,
	CrmFile,
	CrmFileCategory,
	CrmFilePatch,
	CrmLeadCard,
	CrmLeadCreate,
	CrmLeadImport,
	CrmLeadImportResult,
	CrmLeadPatch,
	CrmTask,
	CrmTaskDueFilter,
	CrmTaskInput,
	CrmTaskPatch,
	CrmTaskScope,
	CrmTaskWithLead,
} from "@osmix/contracts";
import type {
	NotificationCampaign,
	NotificationCampaignDraftRequest,
	NotificationCampaignPage,
	NotificationCampaignPreview,
	NotificationCampaignSendRequest,
	NotificationCampaignStats,
	NotificationCampaignUpdateRequest,
	NotificationItem,
	NotificationLocale,
	NotificationPage,
	NotificationRecipientPage,
	WeeklyDigestSettings,
} from "@osmix/contracts/notification";

export type {
	NotificationCampaign,
	NotificationCampaignDraftRequest,
	NotificationCampaignPage,
	NotificationCampaignPreview,
	NotificationCampaignSendRequest,
	NotificationCampaignStats,
	NotificationCampaignStatsByDay,
	NotificationCampaignUpdateRequest,
	NotificationItem,
	NotificationLocale,
	NotificationPage,
	NotificationRecipientPage,
	WeeklyDigestSettings,
} from "@osmix/contracts/notification";

// Э7: «личное дело» продавца — документы верификации и их модерация. DTO
// живёт в contracts (там же его использует api-gateway/sellerDocuments), поэтому
// переиспользуем zod-типы вместо повторного объявления.
// Из корневого barrel, а не из subpath @osmix/contracts/seller-document —
// dev-бандлер admin резолвит subpath-exports по снимку из lockfile, где этого
// экспорта ещё нет; barrel-реэкспорт резолвится по относительному пути.
import type {
	SellerDocument,
	SellerDocumentList,
	SellerDocumentType,
	SellerVerification,
} from "@osmix/contracts";
export type {
	SellerDocument,
	SellerDocumentList,
	SellerDocumentStatus,
	SellerDocumentType,
	SellerVerification,
	SellerDocumentChecklistItem,
	SellerDocumentChecklistStatus,
	InnVerificationResult,
	InnCheckStatus,
} from "@osmix/contracts";

// Геоаналитика потоков площадки (ГДЕ продавцы vs КУДА заказы). DTO живёт в
// contracts (api-gateway/analyticsGeo), поэтому переиспользуем zod-типы. Импорт
// из корневого barrel @osmix/contracts, а не из subpath @osmix/contracts/
// analytics-geo: dev-бандлер admin резолвит subpath-exports по устаревшему
// снимку из lockfile, тогда как barrel-реэкспорт резолвится по относительному
// пути и работает всегда.
import type { GeoFlowAnalytics, MetrikaReport } from "@osmix/contracts";
export type {
	GeoFlowAnalytics,
	GeoFlowOrigin,
	GeoFlowDestination,
	GeoFlowEdge,
	GeoFlowBuyer,
} from "@osmix/contracts";

// Тело ревью документа продавца: модератор принимает или отклоняет с
// примечанием (POST /admin/documents/:id/review).
export type SellerDocumentReviewInput = {
	status: "accepted" | "rejected";
	note?: string;
};

// Э5: центр помощи (fd-support-01) — FAQ-база знаний и обращения (тикеты).
// DTO живёт в contracts (там же его использует api-gateway/support), поэтому
// переиспользуем zod-типы вместо повторного объявления. Импорт из корневого
// barrel @osmix/contracts, а не из subpath @osmix/contracts/support: dev-бандлер
// admin резолвит subpath-exports по устаревшему снимку из lockfile, тогда как
// barrel-реэкспорт резолвится по относительному пути и работает всегда.
import type {
	CreateFaqArticleRequest,
	FaqArticle,
	SupportAttachment,
	SupportTicket,
	SupportTicketDetail,
	SupportTicketMessage,
	SupportTicketStatus,
	TicketMessageRequest,
	UpdateFaqArticleRequest,
} from "@osmix/contracts";
export type {
	CreateFaqArticleRequest,
	FaqArticle,
	FaqAudience,
	SupportAttachment,
	SupportTicket,
	SupportTicketAuthorRole,
	SupportTicketDetail,
	SupportTicketMessage,
	SupportTicketStatus,
	TicketMessageRequest,
	UpdateFaqArticleRequest,
} from "@osmix/contracts";

// Единая управляемая выдача главной (fd-feed-01). DTO живёт в contracts
// (там же его использует api-gateway/feed-blocks) — переиспользуем типы.
// Импорт из корневого barrel @osmix/contracts, а не из subpath
// @osmix/contracts/feed-blocks: dev-бандлер admin резолвит subpath-exports по
// снимку из lockfile, где этого экспорта ещё нет; barrel-реэкспорт резолвится
// по относительному пути и работает всегда.
import type {
	CreateFeedBlockRequest,
	FeedBlockAdmin,
	FeedPlacement,
	PersonalFeedSettings,
	PersonalFeedSettingsPatch,
	UpdateFeedBlockRequest,
} from "@osmix/contracts";
export type {
	CreateFeedBlockRequest,
	FeedBlockAdmin,
	FeedBlockKind,
	FeedDepartment,
	FeedPlacement,
	FeedSegment,
	FeedSlideWire,
	FeedTileWire,
	PersonalFeedSettings,
	PersonalFeedSettingsPatch,
	UpdateFeedBlockRequest,
} from "@osmix/contracts";

export const ProductStatus = z.enum([
	"draft",
	"moderation",
	"rejected",
	"published",
	"blocked",
	"archived",
]);
export type ProductStatus = z.infer<typeof ProductStatus>;

const ProductBadge = z.enum(["handmade", "eco", "new", "limited", "customizable", "bestseller", "sale"]);
const RichDescription = z.object({
	format: z.literal("osmix-rich-text"),
	blocks: z.array(z.object({ type: z.enum(["paragraph", "heading", "bullet"]), text: z.string() })),
});
const ProductAttributeValue = z.union([z.string(), z.number(), z.boolean()]);
const ProductCustomAttribute = z.object({ label: z.string(), value: z.string() });
const ProductCustomization = z.object({
	enabled: z.boolean(),
	options: z.array(z.object({ name: z.string(), minPrice: z.number(), photo: z.string().optional() })),
});
const ProductReturnPolicy = z.object({
	standard14Days: z.boolean(),
	customOrderException: z.boolean(),
	notes: z.string().optional(),
});

const ProductSellerInfo = z.object({
	shopId: z.string(),
	name: z.string(),
	slug: z.string().optional(),
	rating: z.number(),
	ratingCount: z.number(),
});

const ProductModerationEvent = z.object({
	id: z.string(),
	productId: z.string(),
	moderatorId: z.string().optional(),
	actorId: z.string().optional(),
	oldStatus: ProductStatus,
	newStatus: ProductStatus,
	reason: z.string().optional(),
	createdAt: z.string(),
});

export const Product = z.object({
	id: z.string(),
	name: z.string(),
	article: z.string(),
	brand: z.string(),
	// FK to the owning storefront (Storefront.id, e.g. "shop-handmade"). The shop
	// name/slug are resolved at read time from api.storefronts — never
	// denormalised onto the product.
	shopId: z.string(),
	/** Название магазина — приходит в списке консоли (считается на сервере). */
	shopName: z.string().optional(),
	/** Продавец-владелец магазина — ссылка на его карточку. */
	shopOwnerId: z.string().optional(),
	category: z.string(),
	color: z.string(),
	sizes: z.array(z.string()),
	status: ProductStatus,
	sellerInfo: ProductSellerInfo.optional(),
	latestRejectionReason: z.string().optional(),
	moderationHistory: z.array(ProductModerationEvent).optional(),
	price: z.number(),
	oldPrice: z.number().optional(),
	stock: z.number(),
	/** Минимальный заказ, шт. (шлюз, миграция 422); нет - поштучно. */
	minOrderQty: z.number().optional(),
	photos: z.array(z.string()),
	description: z.string(),
	descriptionRich: RichDescription.optional(),
	videoUrl: z.string().optional(),
	categoryAttributes: z.record(z.string(), ProductAttributeValue).optional(),
	customAttributes: z.array(ProductCustomAttribute).optional(),
	customization: ProductCustomization.optional(),
	returnPolicy: ProductReturnPolicy.optional(),
	tags: z.array(z.string()).optional(),
	badges: z.array(ProductBadge).optional(),
	keywords: z.array(z.string()),
	categoryId: z.string().optional(),
	/** Раздел и пол, как их видят фильтры витрины (с наследованием). */
	section: z.string().optional(),
	gender: z.string().optional(),
	facetsInherited: z.boolean().optional(),
	/** Габариты упаковки: граммы и сантиметры. */
	weightGrams: z.number().optional(),
	lengthCm: z.number().optional(),
	widthCm: z.number().optional(),
	heightCm: z.number().optional(),
	/** Документ о соответствии и состояние карточки (17.10.2026). */
	// Схема живёт в контрактах (zod 3); здесь только тип - разбирать нечего, шлюз отдаёт готовое.
	compliance: z.custom<ProductCompliance>((value) => typeof value === "object" && value !== null).optional(),
	madeToOrder: z.boolean().optional(),
	createdAt: z.string(),
	updatedAt: z.string(),
});
export type Product = z.infer<typeof Product>;

export const Meta = z.object({
	page: z.number(),
	pageSize: z.number(),
	total: z.number(),
	totalPages: z.number(),
});
export type Meta = z.infer<typeof Meta>;

// Счётчики карточек по статусам для фильтров раздела «Товары» (+ ключ `all`).
export const ProductStatusCounts = z.record(z.string(), z.number());
export type ProductStatusCounts = z.infer<typeof ProductStatusCounts>;

export const ProductPage = z.object({
	data: z.array(Product),
	meta: Meta,
});
export type ProductPage = z.infer<typeof ProductPage>;

// Справочник типовых причин отклонения товара (GET /admin/products/rejection-reasons).
// Модератор выбирает код из списка и/или пишет свободный комментарий.
export const RejectionReason = z.object({ code: z.string(), label: z.string() });
export type RejectionReason = z.infer<typeof RejectionReason>;

// Тело отклонения товара: типовой код И/ИЛИ свободный комментарий (одно из
// двух обязано присутствовать — это проверяет бэкенд).
export type ProductRejectInput = { reasonCode?: string; reason?: string };

/** Массовое решение модератора по выбранным карточкам. */
export type ProductBulkInput = {
	action: "approve" | "reject" | "block";
	ids: string[];
	reason?: string;
	reasonCode?: string;
};
export type ProductBulkResult = {
	done: string[];
	failed: { id: string; error: string }[];
	/** Массовая публикация пропустила карточки без документа о соответствии. */
	skippedNoDocument?: string[];
};

/** Сводка по документам о соответствии для консоли (17.10.2026). */
export type ComplianceAdminSummary = {
	publishedMissing: number;
	moderationMissing: number;
	held: number;
	requiredCategories: number;
	/** Документы на проверке (31.10.2026). */
	pendingDocuments?: number;
	/** Товары с ручным допуском площадки. */
	admitted?: number;
};
export type ComplianceQueueQuery = {
	status?: "pending" | "approved" | "rejected" | "all";
	search?: string;
	page?: number;
	pageSize?: number;
};
export type ComplianceQueuePage = {
	data: ComplianceDocument[];
	counts: { pending: number; approved: number; rejected: number };
	meta: { page: number; pageSize: number; total: number; totalPages: number };
};
export type ComplianceDocumentProduct = {
	id: string;
	name: string;
	article: string;
	status: string;
	hold: string | null;
	category: string | null;
};
export type ComplianceReportQuery = { shopId?: string; categoryId?: string; status?: string };
export type ComplianceJournalEntry = {
	id: string;
	action: string;
	actorName: string;
	entityKind: string;
	entityId: string;
	reason: string | null;
	metadata: Record<string, unknown>;
	createdAt: string;
};
export type ComplianceJournalPage = {
	data: ComplianceJournalEntry[];
	meta: { page: number; pageSize: number; total: number; totalPages: number };
};
export type {
	CategoryCompliance,
	CategoryComplianceInput,
	ComplianceAdmissionInput,
	ComplianceDocument,
	ComplianceMatrixRow,
	ComplianceReport,
	ProductCompliance,
};

export const UserRole = z.enum([
	"buyer",
	"seller",
	"manufacturer",
	"verified_seller",
	"shop-owner",
	"shop-manager",
	"shop-product-manager",
	"shop-order-manager",
	"shop-support",
	"admin",
]);
export type UserRole = z.infer<typeof UserRole>;

export const OnboardingStatus = z.enum([
	"register_pending",
	"survey_pending",
	"profile_pending",
	"moderation_pending",
	"moderation_passed",
	"active",
]);
export type OnboardingStatus = z.infer<typeof OnboardingStatus>;

export const PlatformRoleSummary = z.object({
	key: z.string(),
	name: z.string(),
	description: z.string().optional(),
	system: z.boolean(),
	// Закрытые разделы в меню - с замком, а не скрыты (роль «Маркетолог», 09.10.2026).
	showLockedNav: z.boolean().optional(),
});
export type PlatformRoleSummary = z.infer<typeof PlatformRoleSummary>;

export const PlatformPermission = z.object({
	key: z.string(),
	category: z.string(),
	label: z.string(),
	description: z.string().optional(),
});
export type PlatformPermission = z.infer<typeof PlatformPermission>;

export const PlatformRole = PlatformRoleSummary.extend({
	permissions: z.array(z.string()),
	userCount: z.number().optional(),
	createdAt: z.string().optional(),
	updatedAt: z.string().optional(),
});
export type PlatformRole = z.infer<typeof PlatformRole>;

export type PlatformRoleInput = {
	key: string;
	name: string;
	description?: string;
	permissions: string[];
	showLockedNav?: boolean;
};

export type PlatformRolePatch = {
	name?: string;
	description?: string | null;
	permissions?: string[];
};

export const User = z.object({
	id: z.string(),
	// Короткий номер пользователя (миграция 285): «ID: 100026» в карточке.
	number: z.number().optional(),
	email: z.string(),
	phone: z.string(),
	name: z.string(),
	shopName: z.string().optional(),
	// The storefront this user owns (Storefront.id), if any. Lets the storefront
	// detail page resolve its owning seller for shop verification.
	shopId: z.string().optional(),
	role: UserRole,
	onboardingStatus: z.string(),
	avatar: z.string().optional(),
	createdAt: z.string(),
	platformRole: PlatformRoleSummary.optional(),
	// Блокировка площадкой (миграция 128): вход закрыт, пока не разблокируют.
	blockedAt: z.string().optional(),
	blockedReason: z.string().optional(),
});
export type User = z.infer<typeof User>;

export const ShopMemberRole = z.enum([
	"shop-owner",
	"shop-manager",
	"shop-product-manager",
	"shop-order-manager",
	"shop-support",
]);
export type ShopMemberRole = z.infer<typeof ShopMemberRole>;

export const StaffAssignableRole = z.enum([
	"shop-manager",
	"shop-product-manager",
	"shop-order-manager",
	"shop-support",
]);
export type StaffAssignableRole = z.infer<typeof StaffAssignableRole>;

export const ShopMember = z.object({
	id: z.string(),
	shopId: z.string(),
	userId: z.string(),
	name: z.string(),
	email: z.string(),
	phone: z.string(),
	role: ShopMemberRole,
	status: z.enum(["active", "removed"]),
	invitedBy: z.string().optional(),
	removedBy: z.string().optional(),
	invitedAt: z.string(),
	joinedAt: z.string().optional(),
	removedAt: z.string().optional(),
});
export type ShopMember = z.infer<typeof ShopMember>;

export const ShopMemberInvite = z.object({
	id: z.string(),
	shopId: z.string(),
	email: z.string(),
	phone: z.string().optional(),
	name: z.string().optional(),
	role: StaffAssignableRole,
	status: z.enum(["pending", "accepted", "revoked", "expired"]),
	invitedBy: z.string().optional(),
	acceptedBy: z.string().optional(),
	revokedBy: z.string().optional(),
	expiresAt: z.string(),
	createdAt: z.string(),
	acceptedAt: z.string().optional(),
	revokedAt: z.string().optional(),
	inviteToken: z.string().optional(),
});
export type ShopMemberInvite = z.infer<typeof ShopMemberInvite>;

export const ShopStaff = z.object({
	members: z.array(ShopMember),
	invites: z.array(ShopMemberInvite),
});
export type ShopStaff = z.infer<typeof ShopStaff>;

// The authenticated principal. Distinct from `User` (the user-management row,
// which carries `createdAt`): this is what /auth/admin-login and /auth/me
// return for the signed-in admin.
export const AuthUser = z.object({
	id: z.string(),
	email: z.string(),
	phone: z.string(),
	name: z.string(),
	shopName: z.string().optional(),
	role: UserRole,
	onboardingStatus: z.string(),
	avatar: z.string().optional(),
	platformRole: PlatformRoleSummary.optional(),
	effectivePermissions: z.array(z.string()).optional(),
});
export type AuthUser = z.infer<typeof AuthUser>;

export const AuthSession = z.object({
	token: z.string(),
	user: AuthUser,
});
export type AuthSession = z.infer<typeof AuthSession>;

export type Credentials = {
	email: string;
	password: string;
};

export const PRODUCT_SORT_KEYS = [
	"name",
	"shop",
	"category",
	"price",
	"stock",
	"status",
	"updated",
	"created",
] as const;
export type ProductSortKey = (typeof PRODUCT_SORT_KEYS)[number];

/** Срез списка товаров без страницы — общий для списка, счётчиков и «выбрать все». */
export type ProductQuery = {
	status?: ProductStatus | "all";
	/** Название, артикул, бренд или название магазина. */
	search?: string;
	/** Только товары одного магазина (выбор товаров в редакторе идеи). */
	shopId?: string;
	/** Категория по id; `__none__` — без категории. */
	categoryId?: string;
	teamLeadId?: string;
	/** Карточка на модерации или по ней ждёт решения заявка на повышение цены. */
	awaiting?: boolean;
	/** «Без документа»: категория требует документ о соответствии, а его нет. */
	compliance?: "missing";
	dateField?: "updated" | "created";
	/** YYYY-MM-DD включительно. */
	from?: string;
	to?: string;
	sort?: ProductSortKey;
	dir?: "asc" | "desc";
};

export type ProductFilter = ProductQuery & {
	status: ProductStatus | "all";
	page: number;
	pageSize: number;
};

export const ProductFilterOptions = z.object({
	shops: z.array(z.object({ id: z.string(), name: z.string(), count: z.number() })),
	categories: z.array(z.object({ id: z.string(), name: z.string(), count: z.number() })),
	teamLeads: z.array(z.object({ id: z.string(), name: z.string() })),
});
export type ProductFilterOptions = z.infer<typeof ProductFilterOptions>;

export type ProductIds = { ids: string[]; total: number };

/** Страница серверного списка консоли. */
export type ServerPage<T> = {
	items: T[];
	total: number;
	page: number;
	pageSize: number;
	totalPages: number;
};

export const USER_SORT_KEYS = ["name", "email", "phone", "role", "onboarding", "created"] as const;
export type UserSortKey = (typeof USER_SORT_KEYS)[number];
export type UserPageFilter = {
	/** Имя, почта, телефон, магазин или точный id. */
	search?: string;
	role?: string;
	/** Только заблокированные. */
	blocked?: boolean;
	sort?: UserSortKey;
	dir?: "asc" | "desc";
	page: number;
	pageSize: number;
};

export const RETURN_SORT_KEYS = ["order", "date", "shop", "buyer", "total", "status", "due"] as const;
export type ReturnSortKey = (typeof RETURN_SORT_KEYS)[number];
export type ReturnPageFilter = {
	status?: string;
	/** Номер заказа или заявки, покупатель, магазин. */
	search?: string;
	/** Только открытые споры. */
	openDisputes?: boolean;
	sort?: ReturnSortKey;
	dir?: "asc" | "desc";
	page: number;
	pageSize: number;
};

export type IdeaListSlice = "all" | "moderation" | "unpublished" | "blocked";
export type IdeaPageFilter = {
	list?: IdeaListSlice;
	shopId?: string;
	/** Порядок по дате создания. */
	dir?: "asc" | "desc";
	page: number;
	pageSize: number;
};

export const ORDER_SORT_KEYS = ["id", "date", "customer", "shop", "items", "total", "status"] as const;
export type OrderSortKey = (typeof ORDER_SORT_KEYS)[number];

/** Срез заказов: поиск, даты и магазин (без статуса — чипы считают по статусам). */
export type OrderQuery = { search?: string; from?: string; to?: string; shopId?: string };

export type OrderPageFilter = OrderQuery & {
	/** Один статус или группа статусов. */
	statuses?: string[];
	/** Точный номер заказа (переход по ссылке `?order=`). */
	id?: string;
	sort?: OrderSortKey;
	dir?: "asc" | "desc";
	page: number;
	pageSize: number;
};

export type UserCreateInput = {
	email: string;
	phone: string;
	name: string;
	password?: string;
	role: UserRole;
	onboardingStatus?: OnboardingStatus;
	platformRoleKey?: string | null;
};

export type UserPasswordInput = {
	password: string;
};

// Admin-editable fields on a user row. Only what's sent changes server-side.
export type UserPatch = {
	name?: string;
	phone?: string;
	shopName?: string;
	role?: UserRole;
	onboardingStatus?: OnboardingStatus;
	platformRoleKey?: string | null;
};

export const CatalogSection = z.enum(["clothing", "shoes", "accessories", "beauty", "home", "kids", "other"]);
export type CatalogSection = z.infer<typeof CatalogSection>;
export const CatalogGender = z.enum(["female", "male", "unisex", "kids"]);
export type CatalogGender = z.infer<typeof CatalogGender>;

export const Category = z.object({
	id: z.string(),
	name: z.string(),
	parentId: z.string().nullable(),
	sortOrder: z.number(),
	/** Раздел и пол самой категории; пусто — наследует от родителя. */
	section: CatalogSection.nullable().optional(),
	gender: CatalogGender.nullable().optional(),
	/** Своя размерная сетка (id из @osmix/catalog-core/size-grids); пусто — наследует. */
	sizeGrid: z.string().nullable().optional(),
	/** Картинка категории для витрины и приложения; пусто — заглушка клиента. */
	imageUrl: z.string().nullable().optional(),
	/** Узел в архиве: скрыт у покупателя и продавца, новых товаров не принимает. */
	archived: z.boolean().optional(),
	/** Товаров в категории (без архива) и из них на витрине. */
	productCount: z.number().optional(),
	publishedCount: z.number().optional(),
	/** Документ о соответствии на самом узле: true / false; null - как у родителя. */
	certRequired: z.boolean().nullable().optional(),
	/** Допустимые виды документа на самом узле; null - как у родителя. */
	certKinds: z.array(z.string()).nullable().optional(),
});

/** Что заденет удаление категории. */
export type CategoryDeleteImpact = { products: number; commissionRates: number; buyerRequests: number };

export type CategoryFacetsInput = { section: CatalogSection | null; gender: CatalogGender | null };
export type Category = z.infer<typeof Category>;

export type CategoryInput = {
	name: string;
	parentId?: string | null;
	sortOrder?: number;
	sizeGrid?: string | null;
	imageUrl?: string | null;
	archived?: boolean;
};

export const CategoryAttributeDefinition = z.object({
	key: z.string(),
	label: z.string(),
	type: z.enum(["text", "number", "boolean", "select"]),
	required: z.boolean().optional(),
	options: z.array(z.string()).optional(),
});
export type CategoryAttributeDefinition = z.infer<typeof CategoryAttributeDefinition>;
export const CategoryAttributeSchema = z.object({
	categoryId: z.string(),
	attributes: z.array(CategoryAttributeDefinition),
	updatedAt: z.string().optional(),
});
export type CategoryAttributeSchema = z.infer<typeof CategoryAttributeSchema>;

export const InspirationFeedItem = z.object({
	id: z.string(),
	productId: z.string(),
	product: Product,
	sortOrder: z.number(),
	editorialTitle: z.string().optional(),
	editorialNote: z.string().optional(),
	createdAt: z.string(),
	updatedAt: z.string(),
});
export type InspirationFeedItem = z.infer<typeof InspirationFeedItem>;

export type InspirationFeedItemInput = {
	productId: string;
	editorialTitle?: string;
	editorialNote?: string;
};

export const InspirationIdea = z.object({
	id: z.string(),
	title: z.string(),
	description: z.string(),
	coverPhoto: z.string(),
	productCount: z.number(),
	createdAt: z.string(),
	updatedAt: z.string(),
});
export type InspirationIdea = z.infer<typeof InspirationIdea>;

export const InspirationIdeaFeedItem = z.object({
	id: z.string(),
	ideaId: z.string(),
	idea: InspirationIdea,
	sortOrder: z.number(),
	createdAt: z.string(),
	updatedAt: z.string(),
});
export type InspirationIdeaFeedItem = z.infer<typeof InspirationIdeaFeedItem>;

// ── Seller-authored ideas (moderation + feed curation) ────────────────────────
// A seller "idea" is a set of pictures, each with shoppable pins pointing at the
// seller's products. The admin moderates them (approve → published / reject) and
// curates which published ones — and in what order — lead the buyer inspiration
// feed (feedRank; null = published but not featured).
export const IdeaStatus = z.enum([
	"draft",
	"moderation",
	"published",
	"rejected",
	"blocked",
	"archived",
]);
export type IdeaStatus = z.infer<typeof IdeaStatus>;

export const IdeaPin = z.object({
	id: z.string(),
	x: z.number(),
	y: z.number(),
	productId: z.string(),
});
export type IdeaPin = z.infer<typeof IdeaPin>;

export const IdeaImage = z.object({
	id: z.string(),
	url: z.string(),
	pins: z.array(IdeaPin),
});
export type IdeaImage = z.infer<typeof IdeaImage>;

export const IdeaAudience = z.enum(["women", "men", "kids", "home"]);
export type IdeaAudience = z.infer<typeof IdeaAudience>;

export const IdeaGroupDiscount = z.object({
	value: z.number(),
	unit: z.enum(["rub", "percent"]),
});
export type IdeaGroupDiscount = z.infer<typeof IdeaGroupDiscount>;

export const IdeaProductSummary = z.object({
	id: z.string(),
	name: z.string(),
	article: z.string(),
	brand: z.string().optional(),
	photo: z.string().optional(),
	color: z.string().optional(),
	sizes: z.array(z.string()),
	price: z.number(),
	oldPrice: z.number().optional(),
	shopId: z.string().optional(),
	status: z.string().optional(),
});
export type IdeaProductSummary = z.infer<typeof IdeaProductSummary>;

export const IdeaMetrics = z.object({
	reach: z.number(),
	impressions: z.number(),
	productClicks: z.number(),
	favorites: z.number(),
	cartAdds: z.number(),
});
export type IdeaMetrics = z.infer<typeof IdeaMetrics>;

export const Idea = z.object({
	id: z.string(),
	shopId: z.string(),
	// Resolved storefront name (the idea carries only shopId).
	shopName: z.string().optional(),
	title: z.string(),
	description: z.string(),
	type: z.string(),
	status: IdeaStatus,
	images: z.array(IdeaImage),
	productCount: z.number(),
	rejectionReason: z.string().optional(),
	// Feed-curation rank: null unless featured in the buyer feed.
	feedRank: z.number().nullable(),
	// Поля редактора по макетам 25.09.2026 (тот же контракт, что у кабинета).
	audience: z.array(IdeaAudience).optional(),
	groupDiscount: IdeaGroupDiscount.nullable().optional(),
	moderatorNote: z.string().optional(),
	products: z.array(IdeaProductSummary).optional(),
	totalPrice: z.number().optional(),
	discountedTotal: z.number().optional(),
	metrics: IdeaMetrics.optional(),
	createdAt: z.string(),
	updatedAt: z.string(),
});
export type Idea = z.infer<typeof Idea>;

export type IdeaFilter = {
	status: IdeaStatus | "all";
};

// Тело создания идеи-вдохновения площадкой (POST /admin/ideas). Автор проставляется
// бэкендом (OSMIX); фото загружаются отдельно через uploadPhoto и приходят сюда
// готовыми URL с товарными метками (пинами).
export type PlatformIdeaType = "inspiration" | "seasonal" | "collaboration";

export type CreatePlatformIdeaInput = {
	title: string;
	description?: string;
	type?: PlatformIdeaType;
	audience?: IdeaAudience[];
	groupDiscount?: IdeaGroupDiscount | null;
	images: {
		id: string;
		url: string;
		pins: { id: string; x: number; y: number; productId: string }[];
	}[];
};

// ── Commerce (orders + carts, read-only for admin) ────────────────────────────
export const CartItem = z.object({
	id: z.string(),
	productId: z.string(),
	shopId: z.string(),
	name: z.string(),
	brand: z.string(),
	photo: z.string(),
	variantId: z.string().optional(),
	variantLabel: z.string().optional(),
	options: z.record(z.string(), z.string()).optional(),
	size: z.string(),
	color: z.string(),
	qty: z.number(),
	unitPrice: z.number(),
	oldUnitPrice: z.number().optional(),
	lineTotal: z.number(),
	stock: z.number(),
	available: z.boolean(),
});
export type CartItem = z.infer<typeof CartItem>;

export const Cart = z.object({
	id: z.string(),
	items: z.array(CartItem),
	itemCount: z.number(),
	subtotal: z.number(),
	updatedAt: z.string(),
	// Покупатель, собравший корзину (если известен по заказу/токену). null —
	// гостевая корзина без привязки к пользователю.
	buyer: z
		.object({ id: z.string(), name: z.string(), phone: z.string() })
		.nullable()
		.optional(),
});
export type Cart = z.infer<typeof Cart>;

const OrderItem = z.object({
	productId: z.string(),
	name: z.string(),
	brand: z.string(),
	photo: z.string(),
	variantId: z.string().optional(),
	variantLabel: z.string().optional(),
	options: z.record(z.string(), z.string()).optional(),
	size: z.string(),
	color: z.string(),
	qty: z.number(),
	unitPrice: z.number(),
	oldUnitPrice: z.number().optional(),
	lineTotal: z.number(),
	/** Тип позиции: `customized` — вещь изготавливают руками. */
	kind: z.enum(["regular", "madeToOrder", "customized"]).optional(),
	description: z.string().optional(),
	/** Характеристики позиции; выбранные изменения идут первыми строками. */
	specs: z.array(z.object({ label: z.string(), value: z.string() })).optional(),
	productionEstimate: z.string().optional(),
});

const OrderGroup = z.object({
	shopId: z.string(),
	shopName: z.string(),
	shopSlug: z.string(),
	items: z.array(OrderItem),
	subtotal: z.number(),
	deliveryFee: z.number(),
	// The delivery choice the buyer made for this shop at checkout. Present when
	// the order carries a resolved carrier/method (СДЭК live tariff or flat
	// fallback); absent on older orders.
	shipment: z
		.object({
			carrier: z.enum(["sdek", "flat", "self", "pecom", "dellin", "seller_pickup"]),
			method: z.enum(["standard", "express", "pickup"]).optional(),
			tariffCode: z.number().optional(),
			tariffName: z.string().optional(),
			deliveryDays: z.string().optional(),
			toPickupPoint: z.boolean().optional(),
			pvzCode: z.string().optional(),
			pvzAddress: z.string().optional(),
			pvzWorkTime: z.string().optional(),
			// Самовывоз от продавца: снимок точки на момент оформления.
			pickupPoint: z
				.object({
					id: z.string(),
					name: z.string(),
					city: z.string(),
					address: z.string(),
					schedule: z.string(),
					phone: z.string(),
					instructions: z.string(),
					readyLabel: z.string(),
					price: z.number(),
					storageDays: z.number(),
				})
				.optional(),
		})
		.optional(),
});

// "pending" (awaiting payment) is filtered out of the admin Orders list, but
// kept in the enum so status maps stay exhaustive against the shared shape.
export const OrderStatus = z.enum([
	"pending",
	"declined",
	"paid",
	"processing",
	"new",
	"confirmed",
	"in_work",
	"ready_to_ship",
	"shipped",
	"in_transit",
	// Привезли в пункт выдачи — ждём покупателя (в отличие от складского
	// `ready_to_ship`, который про готовность ПРОДАВЦА отдать заказ).
	"awaiting_pickup",
	"delivered",
	"completed",
	"cancelled",
	"return_requested",
	"dispute_open",
	"archived",
	// §2: «Ждут оплаты» — добавлен аддитивно вслед за общим контрактом.
	"awaiting_payment",
]);
export type OrderStatus = z.infer<typeof OrderStatus>;

const OrderStatusEvent = z.object({
	id: z.string(),
	orderId: z.string(),
	actorType: z.enum(["system", "buyer", "seller", "admin", "carrier"]),
	actorId: z.string().optional(),
	oldStatus: z.string(),
	newStatus: z.string(),
	reason: z.string().optional(),
	meta: z.record(z.string(), z.unknown()),
	createdAt: z.string(),
});

const OrderReview = z.object({
	id: z.string(),
	orderId: z.string(),
	buyerUserId: z.string().optional(),
	rating: z.number(),
	text: z.string(),
	createdAt: z.string(),
	updatedAt: z.string(),
});

const OrderReturnRequest = z.object({
	id: z.string(),
	orderId: z.string(),
	buyerUserId: z.string().optional(),
	kind: z.enum(["return", "dispute"]),
	reason: z.string(),
	status: z.string(),
	createdAt: z.string(),
	updatedAt: z.string(),
});

export const Order = z.object({
	id: z.string(),
	// Короткий номер заказа для людей (миграция 285, правка №13): 100245.
	// `id` остаётся ключом адресов и запросов.
	number: z.number().optional(),
	status: OrderStatus,
	// Покупатель как пользователь платформы (для перехода в его карточку из
	// заказов). Может отсутствовать у гостевых/исторических заказов.
	buyerUserId: z.string().optional(),
	contact: z.object({ name: z.string(), email: z.string(), phone: z.string() }),
	address: z.object({
		country: z.string(),
		city: z.string(),
		street: z.string(),
		postalCode: z.string(),
		comment: z.string().optional(),
	}),
	delivery: z.enum(["standard", "express", "pickup"]),
	payment: z.object({
		provider: z.string().optional(),
		status: z.string(),
		transactionId: z.string().optional(),
		brand: z.string(),
		last4: z.string(),
	}),
	// Способ оплаты: "invoice" — заказ ЮЛ, ожидающий оплаты по счёту (админ
	// подтверждает поступление вручную). Остаток к оплате после списания с баланса.
	paymentMethod: z.string().optional(),
	payableTotal: z.number().optional(),
	balanceApplied: z.number().optional(),
	groups: z.array(OrderGroup),
	subtotal: z.number(),
	// Скидка площадки (промокод OSMIX): вычитается из суммы товаров. Без неё
	// «Итого» в карточке не сходилось с суммой строк.
	discount: z.number().optional(),
	deliveryTotal: z.number(),
	total: z.number(),
	itemCount: z.number(),
	createdAt: z.string(),
	paidAt: z.string().optional(),
	readyToShipAt: z.string().optional(),
	trackingNumber: z.string().optional(),
	cancelledAt: z.string().optional(),
	completedAt: z.string().optional(),
	statusHistory: z.array(OrderStatusEvent).optional(),
	review: OrderReview.optional(),
	returnRequests: z.array(OrderReturnRequest).optional(),
	refund: z
		.object({
			// "refunded" — деньги вернулись в PSP, "external_settlement" — возврат
			// вне системы, "mock_recorded" — заказы, отменённые до 17.09.2026,
			// "pending" — возврат отправлен в API провайдера (fd-order-18).
			status: z.enum(["mock_recorded", "refunded", "external_settlement", "pending"]),
			provider: z.string().optional(),
			transactionId: z.string().optional(),
			amount: z.number().optional(),
			reason: z.string().optional(),
			createdAt: z.string(),
		})
		.optional(),
});
export type Order = z.infer<typeof Order>;

// Статусы, в которые администратор может перевести заказ вручную
// (POST /admin/orders/:id/status). Переходы проверяет шлюз по той же матрице,
// что и у продавца; консоль лишь не предлагает заведомо невозможного.
export const AdminOrderTargetStatus = z.enum([
	"confirmed",
	"in_work",
	"ready_to_ship",
	"shipped",
	"in_transit",
	"delivered",
	"completed",
	"cancelled",
	"return_requested",
	"dispute_open",
	"archived",
]);
export type AdminOrderTargetStatus = z.infer<typeof AdminOrderTargetStatus>;

// Состав фискального чека заказа (54-ФЗ), как его отдаёт GET /orders/:id/receipt:
// позиции со скидкой, разнесённой по строкам, плюс доставка.
export const OrderReceipt = z.object({
	Items: z.array(
		z.object({
			label: z.string(),
			price: z.number(),
			quantity: z.number(),
			amount: z.number(),
		}),
	),
	email: z.string().optional(),
	phone: z.string().optional(),
	amounts: z.record(z.string(), z.number()).optional(),
});
export type OrderReceipt = z.infer<typeof OrderReceipt>;

// ── Возвраты (fd-order-19..22) ───────────────────────────────────────────────
// Заявка на возврат/спор с собственным жизненным циклом
// requested → approved/rejected → received → refunded. Соответствует
// ReturnRequest из api-gateway (services/returns.ts). Администратор видит все
// заявки, разбирает мультимагазинные и просроченные продавцом.
export const ReturnStatus = z.enum([
	"requested",
	"approved",
	"rejected",
	"received",
	"refunded",
]);
export type ReturnStatus = z.infer<typeof ReturnStatus>;

export const ReturnRequest = z.object({
	id: z.string(),
	orderId: z.string(),
	// Короткие номера заказа и покупателя (миграция 285) — их и показываем.
	orderNumber: z.number().optional(),
	buyerNumber: z.number().optional(),
	shopId: z.string().optional(),
	shopName: z.string().optional(),
	buyerUserId: z.string().optional(),
	buyerName: z.string().optional(),
	kind: z.enum(["return", "dispute"]),
	reason: z.string(),
	// Код причины: not_suitable, defect, wrong_or_incomplete, damaged или
	// other («Другое», комментарий покупателя обязателен — правка №14).
	reasonCode: z.string().optional(),
	status: ReturnStatus,
	dueAt: z.string().optional(),
	overdue: z.boolean(),
	daysLeft: z.number(),
	justified: z.boolean().optional(),
	resolvedBy: z.string().optional(),
	resolvedByName: z.string().optional(),
	resolvedAt: z.string().optional(),
	resolutionNote: z.string().optional(),
	refundGoods: z.number(),
	refundDelivery: z.number(),
	refundTotal: z.number(),
	goodsReceivedAt: z.string().optional(),
	refundedAt: z.string().optional(),
	// Возврат прошёл через платёжный сервис: провайдер и id транзакции — след
	// чека возврата. Пусто при ручном перечислении.
	refundProvider: z.string().optional(),
	refundTransactionId: z.string().optional(),
	// Робокасса по ключам магазина деньги не возвращает — возврат делается в её
	// личном кабинете. Шлюз отдаёт, что и где вернуть: номер заказа, номер
	// счёта Робокассы (InvId) и сумму.
	providerRefund: z
		.object({
			provider: z.literal("robokassa"),
			invoiceId: z.number().optional(),
			orderNumber: z.number().optional(),
			amount: z.number(),
			// Подключён API возвратов (fd-order-18): деньги уйдут кнопкой.
			api: z.boolean().optional(),
		})
		.optional(),
	// Последняя заявка на возврат через API провайдера (fd-order-18).
	paymentRefund: z
		.object({
			id: z.string(),
			status: z.enum(["queued", "processing", "succeeded", "failed", "uncertain", "manual"]),
			providerState: z.string().optional(),
			lastError: z.string().optional(),
			attempts: z.number(),
			requestId: z.string().optional(),
		})
		.optional(),
	// Спор по заявке: жалоба покупателя, ответ продавца и решение площадки.
	dispute: z
		.object({
			status: z.enum(["open", "closed"]),
			buyerComment: z.string(),
			sellerText: z.string().optional(),
			sellerRespondedAt: z.string().optional(),
			outcome: z.enum(["buyer", "seller", "partial"]).optional(),
		})
		.optional(),
	createdAt: z.string(),
	updatedAt: z.string(),
});
export type ReturnRequest = z.infer<typeof ReturnRequest>;

const ReturnAttachment = z.object({
	id: z.string(),
	author: z.enum(["buyer", "seller"]),
	kind: z.enum(["photo", "video"]),
	url: z.string(),
	thumbnailUrl: z.string().optional(),
	createdAt: z.string(),
});

// Полная карточка заявки (GET /admin/returns/:id): позиции, фото сторон и спор.
export const ReturnDetail = ReturnRequest.extend({
	// Сколько просит покупатель по заявленным позициям. До решения refundTotal
	// равен нулю, и без этого поля карточка показывала «к возврату 0».
	requestedRefund: z.number().optional(),
	lines: z.array(
		z.object({
			id: z.string(),
			productId: z.string(),
			name: z.string(),
			photo: z.string().optional(),
			variant: z.string().optional(),
			quantity: z.number(),
			unitPrice: z.number(),
			refund: z.number(),
			reasonCode: z.string(),
			comment: z.string(),
			cancelledAt: z.string().optional(),
			attachments: z.array(ReturnAttachment).default([]),
		}),
	),
	attachments: z.array(ReturnAttachment).default([]),
	dispute: z
		.object({
			id: z.string(),
			status: z.enum(["open", "closed"]),
			buyerComment: z.string(),
			sellerText: z.string().optional(),
			sellerRespondedAt: z.string().optional(),
			outcome: z.enum(["buyer", "seller", "partial"]).optional(),
			openedAt: z.string(),
			closedAt: z.string().optional(),
		})
		.optional(),
});
export type ReturnDetail = z.infer<typeof ReturnDetail>;
export type DisputeOutcome = "buyer" | "seller" | "partial";

export const ReturnsSummary = z.object({
	statuses: z.array(z.object({ status: z.string(), count: z.number() })),
	overdue: z.number(),
	total: z.number(),
	/** Открытые споры по всем заявкам (счётчик кнопки «Открытые споры»). */
	openDisputes: z.number().optional(),
});
export type ReturnsSummary = z.infer<typeof ReturnsSummary>;

// Тело решения по заявке. `justified` обязателен при `approve: true` —
// от него зависит компенсация доставки покупателю.
export type ReturnResolveInput = { approve: boolean; justified?: boolean; note?: string };

// A carrier shipment for one shop's portion of an order. The gateway creates
// one per shop group when a carrier (СДЭК) is used; flat-rate orders have no
// shipment. `state`/`statusCode`/`statusName` mirror the carrier's tracking;
// `cdekNumber` is the trackable number, absent until СДЭК registers the order.
export const OrderShipment = z.object({
	id: z.string(),
	orderId: z.string(),
	shopId: z.string(),
	carrier: z.enum(["sdek", "flat", "self", "pecom", "dellin", "seller_pickup"]),
	sdekUuid: z.string().optional(),
	cdekNumber: z.string().optional(),
	tariffCode: z.number().optional(),
	deliveryPoint: z.string().optional(),
	deliverySum: z.number().optional(),
	state: z.string(),
	statusCode: z.string().optional(),
	statusName: z.string().optional(),
	statusAt: z.string().optional(),
	statusHistory: z.array(
		z.object({
			code: z.string().optional(),
			name: z.string().optional(),
			at: z.string().optional(),
		}),
	),
	createdAt: z.string(),
	updatedAt: z.string(),
});
export type OrderShipment = z.infer<typeof OrderShipment>;

// ── Shops (seller-shop moderation) ───────────────────────────────────────────
// A "shop" here is a seller plus the onboarding submission they filled in at
// seller-web /profile. The admin reviews it and approves (→ verified_seller),
// rejects (back to filling), or blocks.
export const SellerShopStatus = z.enum([
	"filling",
	"on_moderation",
	"active",
	"blocked",
]);
export type SellerShopStatus = z.infer<typeof SellerShopStatus>;

// П7 (fd-onboard-25/26/30): разделы анкеты продавца со своими статусами.
export const ProfileSectionKey = z.enum(["about", "requisites", "documents"]);
export type ProfileSectionKey = z.infer<typeof ProfileSectionKey>;
export const ProfileSectionState = z.object({
	section: ProfileSectionKey,
	status: z.enum(["draft", "submitted", "accepted", "rework"]),
	remark: z.string().optional(),
	changedAt: z.string().optional(),
	reviewedAt: z.string().optional(),
	/** Продавец правил раздел после последнего решения - пометка «изменён после проверки». */
	changedAfterReview: z.boolean(),
});
export type ProfileSectionState = z.infer<typeof ProfileSectionState>;
export type ProfileSectionReviewInput =
	| { status: "accepted" }
	| { status: "rework"; remark: string; deadline?: string | null };

export const SellerManagerContact = z.object({
	id: z.string(),
	name: z.string(),
	email: z.string(),
	phone: z.string(),
});
export type SellerManagerContact = z.infer<typeof SellerManagerContact>;

export const SellerShop = z.object({
	id: z.string(),
	// Storefront catalog id this seller owns (Storefront.id), or null while
	// still onboarding. Joins a seller row to its catalog storefront.
	shopId: z.string().nullable(),
	shopName: z.string(),
	sellerName: z.string(),
	email: z.string(),
	phone: z.string(),
	role: z.string(),
	status: SellerShopStatus,
	onboardingStatus: z.string(),
	submittedAt: z.string().nullable(),
	rejectionReason: z.string().optional(),
	createdAt: z.string(),
	manager: SellerManagerContact.nullable(),
	/** Бейдж «Проверенный продавец» (Э7). Опционально — см. Storefront.verified. */
	verified: z.boolean().optional(),
});
export type SellerShop = z.infer<typeof SellerShop>;

// The seller's submitted sections. Shapes are loose (forms evolve on the seller
// side); the admin renders them read-only, so every field is optional.
export const SellerSurvey = z
	.object({
		businessForm: z.string(),
		role: z.string(),
		categories: z.array(z.string()),
		salesChannels: z.array(z.string()),
		contact: z
			.object({
				name: z.string(),
				city: z.string(),
				phone: z.string(),
				preferredContact: z.string(),
				preferredTimeSlots: z.array(z.string()),
			})
			.partial(),
	})
	.partial();
export type SellerSurvey = z.infer<typeof SellerSurvey>;

export const ShopProfile = z
	.object({
		shopName: z.string(),
		foundingDate: z.string(),
		brandDescription: z.string(),
		mainProduct: z.string(),
		role: z.string(),
		roleCustom: z.string(),
		categories: z.array(z.string()),
		salesChannels: z.array(z.string()),
		salesChannelsCustom: z.string(),
		socials: z.record(z.string(), z.string()),
	})
	.partial();
export type ShopProfile = z.infer<typeof ShopProfile>;

export const Requisites = z
	.object({
		inn: z.string(),
		shortName: z.string(),
		fullName: z.string(),
		organizationForm: z.string(),
		legalAddress: z.string(),
		bankAccount: z.string(),
		bik: z.string(),
		kpp: z.string(),
		ogrn: z.string(),
		accountantContact: z.string(),
	})
	.partial();
export type Requisites = z.infer<typeof Requisites>;

export const ProfileDocument = z.object({
	id: z.string(),
	name: z.string(),
	required: z.boolean().optional(),
	fileName: z.string().optional(),
	uploadedAt: z.string().optional(),
	status: z.string(),
});
export type ProfileDocument = z.infer<typeof ProfileDocument>;

export const Founder = z.object({
	id: z.string(),
	fullName: z.string().optional(),
	position: z.string().optional(),
	phone: z.string().optional(),
	email: z.string().optional(),
});
export type Founder = z.infer<typeof Founder>;

// Тимлид магазина — сотрудник платформы, который курирует модерацию товаров
// этого магазина. Отдельная роль от менеджера-куратора продавца (тот ведёт
// онбординг). Приходит в детали магазина; назначается PUT/DELETE
// /admin/shops/:id/team-lead.
export const ShopTeamLead = z.object({
	userId: z.string(),
	name: z.string(),
	email: z.string(),
	assignedAt: z.string(),
});
export type ShopTeamLead = z.infer<typeof ShopTeamLead>;

export const SellerShopDetail = SellerShop.extend({
	survey: SellerSurvey.nullable(),
	shop: ShopProfile,
	requisites: Requisites,
	documents: z.array(ProfileDocument),
	founders: z.array(Founder),
	teamLead: ShopTeamLead.nullable(),
});
export type SellerShopDetail = z.infer<typeof SellerShopDetail>;

// ── Storefronts (the marketplace shops products belong to) ────────────────────
// A buyer-facing storefront from the gateway catalog (`GET /shops`). Distinct
// from SellerShop above: SellerShop is the seller-onboarding/moderation record
// keyed by user id; a Storefront is the addressable shop (keyed by its own
// slug) that products reference via `Product.shopId`.
export const StorefrontStatus = z.enum(["active", "blocked"]);
export type StorefrontStatus = z.infer<typeof StorefrontStatus>;

/**
 * Internal onboarding checks a shop must clear before it can trade — «он
 * внутреннюю нашу проверку должен ещё пройти». Distinct from `status`, which only
 * says whether the shop is blocked. MOCK-ONLY: the gateway has no such record.
 */
export const VERIFICATION_STEPS = [
	"documents",
	"legal",
	"accounting",
	"catalog",
	"launched",
] as const;
export type VerificationStepKey = (typeof VERIFICATION_STEPS)[number];

export const VerificationStep = z.object({
	key: z.enum(VERIFICATION_STEPS),
	done: z.boolean(),
	/** ISO timestamp of completion, absent while pending. */
	at: z.string().optional(),
	by: z.string().optional(),
});
export type VerificationStep = z.infer<typeof VerificationStep>;

export const VerificationComment = z.object({
	id: z.string(),
	at: z.string(),
	author: z.string(),
	text: z.string(),
});
export type VerificationComment = z.infer<typeof VerificationComment>;

export const StorefrontVerification = z.object({
	steps: z.array(VerificationStep),
	comments: z.array(VerificationComment),
});
export type StorefrontVerification = z.infer<typeof StorefrontVerification>;

export const Storefront = z.object({
	id: z.string(),
	slug: z.string(),
	name: z.string(),
	tagline: z.string(),
	description: z.string(),
	logoUrl: z.string().optional(),
	heroUrl: z.string(),
	rating: z.number(),
	ratingCount: z.number(),
	productCount: z.number(),
	location: z.string(),
	since: z.string(),
	status: StorefrontStatus,
	/** Optional so the live gateway (which never returns it) still parses. */
	verification: StorefrontVerification.optional(),
	/**
	 * Итоговый бейдж «Проверенный продавец» (Э7, fd-onboard-12): документы
	 * приняты + ИНН проверен + продавец активен. Опционально — старая отдача
	 * без поля продолжает парситься.
	 */
	verified: z.boolean().optional(),
});
export type Storefront = z.infer<typeof Storefront>;

// Сводка продаж по магазину — для сегментации списка магазинов по обороту.
export const ShopSalesSummary = z.object({
	shopId: z.string(),
	shopSlug: z.string().nullable(),
	shopName: z.string(),
	gmv: z.number(),
	orders: z.number(),
	lastOrderAt: z.string().nullable(),
});
export type ShopPartnerCard = { shopId: string; slug: string; name: string; logoUrl?: string; productCount: number };
export type ShopPartners = { partners: ShopPartnerCard[]; partnerOf: ShopPartnerCard[] };

/** Самовывоз от продавца: выключатель и точки выдачи магазина (GET /admin/shops/:shopId/pickup-points). */
export type ShopPickupPoints = {
	shopId: string;
	enabled: boolean;
	points: {
		id: string;
		name: string;
		city: string;
		address: string;
		schedule: string;
		phone: string;
		instructions: string;
		readyAmount: number;
		readyUnit: "hours" | "days";
		price: number;
		storageDays: number;
		enabled: boolean;
	}[];
};

export type ShopSalesSummary = z.infer<typeof ShopSalesSummary>;

// Полная аналитика магазина для богатой карточки (статистика + визуал).
export const ShopAnalytics = z.object({
	shopId: z.string(),
	shopName: z.string(),
	shopSlug: z.string().nullable(),
	createdAt: z.string().nullable(),
	ordersTotal: z.number(),
	ordersPaid: z.number(),
	ordersCancelled: z.number(),
	gmv: z.number(),
	avgCheck: z.number(),
	commissionRate: z.number(),
	commission: z.number(),
	returnsCount: z.number(),
	returnRate: z.number(),
	buyersCount: z.number(),
	firstOrderAt: z.string().optional(),
	lastOrderAt: z.string().optional(),
	daysSinceLastOrder: z.number().optional(),
	topProducts: z.array(z.object({ name: z.string(), qty: z.number(), revenue: z.number() })),
	revenueSeries: z.array(z.object({ date: z.string(), value: z.number() })),
	recentOrders: z.array(
		z.object({
			id: z.string(),
			date: z.string(),
			buyerName: z.string(),
			total: z.number(),
			status: z.string(),
			thumbs: z.array(z.string()),
		}),
	),
	/** Состояние каталога магазина: карточки по статусам, склад, продажи, рейтинг. */
	catalog: z
		.object({
			productsTotal: z.number(),
			productsPublished: z.number(),
			productsModeration: z.number(),
			productsRejected: z.number(),
			productsDraft: z.number(),
			stockUnits: z.number(),
			unitsSold: z.number(),
			rating: z.number(),
			reviewsCount: z.number(),
		})
		.optional(),
});
export type ShopAnalytics = z.infer<typeof ShopAnalytics>;

// Заявка продавца на выплату. Воронка: «Новая → Одобрена → Выплачена», либо
// «Отклонена» — и всегда с причиной, иначе продавец не поймёт, что исправлять.
export const PayoutRequest = z.object({
	id: z.string(),
	shopId: z.string(),
	shopName: z.string().optional(),
	period: z.string(),
	amount: z.number(),
	status: z.enum(["new", "approved", "rejected", "paid"]),
	sellerComment: z.string().optional(),
	adminComment: z.string().optional(),
	decidedAt: z.string().optional(),
	paidAt: z.string().optional(),
	createdAt: z.string(),
	/**
	 * Снимок на момент заявки (миграция 111): начислено за месяц и доступно к
	 * выводу. «Не запрошено» — остаток, который продавец не забрал: кредиторка
	 * площадки перед ним. У заявок до миграции снимка нет.
	 */
	accruedAtRequest: z.number().optional(),
	availableAtRequest: z.number().optional(),
	notRequested: z.number().optional(),
	/** Номер платёжного поручения — выдаётся при первой выгрузке в 1С. */
	paymentOrderNumber: z.number().optional(),
	exportedAt: z.string().optional(),
	/** Счёт, выбранный продавцом в окне вывода (миграция 281): «Р/с **** 1234». */
	account: z
		.object({
			id: z.string(),
			label: z.string(),
			last4: z.string(),
			bankName: z.string().optional(),
			bik: z.string().optional(),
		})
		.optional(),
	/** Состояние контрагента в реестре на момент просмотра очереди. */
	counterparty: z
		.object({
			registryStatus: z.string(),
			active: z.boolean(),
			unknown: z.boolean(),
			inn: z.string().optional(),
			name: z.string().optional(),
		})
		.optional(),
});
export type PayoutRequest = z.infer<typeof PayoutRequest>;
// Расшифровка заявки: из чего сложилась сумма и какие заказы за ней стоят.
// Считается шлюзом теми же запросами, что и отчёт продавца, — цифры совпадают.
export const PayoutDetails = z.object({
	request: PayoutRequest,
	statement: z.object({
		period: z.string(),
		sales: z.number(),
		commission: z.number(),
		refunds: z.number(),
		// Доставка силами продавца («Доставляю сам», «Самовывоз»): ему, без комиссии.
		sellerDelivery: z.number().optional(),
		accrued: z.number(),
		paid: z.number(),
		pending: z.number(),
		available: z.number(),
	}),
	orders: z.array(
		z.object({
			id: z.string(),
			number: z.number().optional(),
			createdAt: z.string(),
			status: z.string(),
			goods: z.number(),
			refunded: z.number(),
		}),
	),
	sellerUserId: z.string().optional(),
});
export type PayoutDetails = z.infer<typeof PayoutDetails>;
/** Реквизиты стороны платёжного поручения. */
export type BankRequisites = {
	name: string;
	inn: string;
	kpp: string;
	account: string;
	bik: string;
	bankName: string;
	corrAccount: string;
	bankCity?: string;
};

/** Реестр платёжных поручений по одобренным заявкам (выгрузка для 1С). */
export type PaymentRegistry = {
	lines: {
		request: PayoutRequest;
		recipient: BankRequisites;
		/** Чего не хватает в реквизитах продавца; пусто — строка уйдёт в файл. */
		problems: string[];
		ready: boolean;
		purpose: string;
	}[];
	payer: BankRequisites | null;
	/** Переменные окружения с реквизитами площадки, которых не хватает. */
	missingPayerEnv: string[];
	total: number;
	readyTotal: number;
};

export type CashFlowSection = "receipts" | "refunds" | "commission" | "payouts";

/** «Движение денежных средств» за период — из реальных таблиц шлюза. */
export type CashFlowReport = {
	from: string;
	to: string;
	rows: {
		date: string;
		section: CashFlowSection;
		direction: 1 | -1 | 0;
		counterparty: string;
		document: string;
		amount: number;
		comment: string;
	}[];
	totals: { receipts: number; refunds: number; payouts: number; commission: number; net: number };
	byMonth: {
		month: string;
		receipts: number;
		refunds: number;
		payouts: number;
		commission: number;
		net: number;
	}[];
};

/** Скачанный файл выгрузки: содержимое, имя и сколько строк вошло/пропущено. */
export type DownloadedFile = { blob: Blob; filename: string; rows: number; skipped: number };

// ── Бухгалтерия и 1С (поток 1С, 25.09.2026) ───────────────────────────────

/** Выгрузки раздела «Финансы → Бухгалтерия». */
export type AccountingDataset = "sales" | "returns" | "agent-reports" | "commission-acts" | "settlements";

/** Фильтр выгрузки: дни YYYY-MM-DD (реестры), месяц YYYY-MM (отчёт агента, акты), месяцы from/to (взаиморасчёты). */
export type AccountingQuery = { from?: string; to?: string; period?: string; shopId?: string };

export type AccountingShop = { shopId: string; name: string };

export type AccountingSeller = {
	shopId: string;
	shopName: string;
	legalName: string;
	inn: string;
	kpp: string;
	vat: string;
};

export type AccountingSalesSummary = {
	from: string;
	to: string;
	linesTotal: number;
	totals: {
		orders: number;
		qty: number;
		goods: number;
		sellerDiscount: number;
		promoDiscount: number;
		delivery: number;
		paidByBuyer: number;
		commission: number;
		returned: number;
	};
};

export type AccountingReturnsSummary = {
	from: string;
	to: string;
	lines: {
		returnId: string;
		orderId: string;
		status: string;
		refundedAt?: string;
		requestedAt: string;
		seller: AccountingSeller;
		items: string;
		total: number;
	}[];
	totals: { count: number; refunded: number; goods: number; delivery: number; total: number };
};

export type AgentReportSummary = {
	number: string;
	period: string;
	date: string;
	seller: AccountingSeller;
	agentMissing: string[];
	ordersCount: number;
	summary: {
		opening: number;
		sales: number;
		refunds: number;
		commission: number;
		expenses: number;
		penalties: number;
		accrued: number;
		paidForPeriod: number;
		paidInMonth: number;
		pending: number;
		due: number;
		closing: number;
		deliveryTransit: number;
		ordersCount: number;
	};
};

export type CommissionActSummary = {
	number: string;
	period: string;
	seller: AccountingSeller;
	sales: number;
	refunds: number;
	commission: number;
	vatLabel: string;
	vat: number;
};

export type SettlementsReport = {
	from: string;
	to: string;
	rows: {
		shopId: string;
		seller: AccountingSeller;
		opening: number;
		accrued: number;
		sales: number;
		commission: number;
		refunds: number;
		paid: number;
		closing: number;
		pending: number;
	}[];
	totals: {
		opening: number;
		accrued: number;
		sales: number;
		commission: number;
		refunds: number;
		paid: number;
		closing: number;
		pending: number;
	};
};

export type AccountingJournalEntry = {
	id: string;
	dataset: string;
	format: string;
	filters: Record<string, unknown>;
	rows: number;
	createdAt: string;
	who: string;
};

/** Статус ЗАЯВКИ продавца (не путать с PayoutStatus внутреннего контура выплат). */
export type PayoutRequestStatus = PayoutRequest["status"];

// Денежные настройки площадки. Ставки — доли (0.12 = 12 %). Ставка магазина
// сильнее ставки категории, ставка категории — базовой.
export const FinanceSettings = z.object({
	commissionRate: z.number(),
	commissionByCategory: z.record(z.string(), z.number()),
	commissionByShop: z.record(z.string(), z.number()),
	settlementDays: z.number(),
	currency: z.string(),
	carrierName: z.string(),
	carrierCadence: z.string(),
});
export type FinanceSettings = z.infer<typeof FinanceSettings>;

// ── Раздел «Комиссии» (встреча 25.09.2026) ─────────────────────────────────
// Ставки — доли (0.065 = 6,5 %). `rate: null` — своей ставки нет, действует
// унаследованная; `effectiveRate` приходит всегда.
// Приоритет: магазин×категория → магазин на всё → категория (с подъёмом по
// дереву) → базовая.
export const CommissionSource = z.enum(["shop_category", "shop", "category", "base"]);
export type CommissionSource = z.infer<typeof CommissionSource>;

export const CommissionWindow = z.object({ days: z.number(), from: z.string(), to: z.string() });
export type CommissionWindow = z.infer<typeof CommissionWindow>;

export const CommissionCategoryRow = z.object({
	id: z.string(),
	name: z.string(),
	parentId: z.string().nullable(),
	depth: z.number(),
	sortOrder: z.number(),
	rate: z.number().nullable(),
	effectiveRate: z.number(),
	inheritedFromId: z.string().nullable(),
	shopCount: z.number(),
	personalCount: z.number(),
	productCount: z.number(),
	sales: z.number(),
});
export type CommissionCategoryRow = z.infer<typeof CommissionCategoryRow>;

export const CommissionOverview = z.object({
	baseRate: z.number(),
	window: CommissionWindow,
	categories: CommissionCategoryRow.array(),
	shopsWithOwnRate: z.number(),
	personalTotal: z.number(),
});
export type CommissionOverview = z.infer<typeof CommissionOverview>;

export const CommissionShopCategory = z.object({
	categoryId: z.string(),
	name: z.string(),
	path: z.string(),
	productCount: z.number(),
	sales: z.number(),
	rate: z.number().nullable(),
	effectiveRate: z.number(),
	source: CommissionSource,
	sourceCategoryId: z.string().nullable(),
});
export type CommissionShopCategory = z.infer<typeof CommissionShopCategory>;

export const CommissionShopRow = z.object({
	id: z.string(),
	name: z.string(),
	slug: z.string(),
	inn: z.string().nullable(),
	status: z.string(),
	productCount: z.number(),
	sales: z.number(),
	shopRate: z.number().nullable(),
	personalCount: z.number(),
	categories: CommissionShopCategory.array(),
});
export type CommissionShopRow = z.infer<typeof CommissionShopRow>;

export const CommissionShopList = z.object({
	baseRate: z.number(),
	window: CommissionWindow,
	shops: CommissionShopRow.array(),
});
export type CommissionShopList = z.infer<typeof CommissionShopList>;

export const CommissionCategoryShop = z.object({
	shopId: z.string(),
	name: z.string(),
	slug: z.string(),
	inn: z.string().nullable(),
	status: z.string(),
	productCount: z.number(),
	sales: z.number(),
	rate: z.number().nullable(),
	shopRate: z.number().nullable(),
	effectiveRate: z.number(),
	source: CommissionSource,
	sourceCategoryId: z.string().nullable(),
});
export type CommissionCategoryShop = z.infer<typeof CommissionCategoryShop>;

export const CommissionCategoryShops = z.object({
	category: CommissionCategoryRow,
	window: CommissionWindow,
	shops: CommissionCategoryShop.array(),
});
export type CommissionCategoryShops = z.infer<typeof CommissionCategoryShops>;

export const CommissionHistoryEntry = z.object({
	id: z.string(),
	createdAt: z.string(),
	actorName: z.string(),
	scope: z.enum(["base", "category", "shop", "shop_category"]),
	shopId: z.string().nullable(),
	shopName: z.string().nullable(),
	categoryId: z.string().nullable(),
	categoryName: z.string().nullable(),
	fromRate: z.number().nullable(),
	toRate: z.number().nullable(),
});
export type CommissionHistoryEntry = z.infer<typeof CommissionHistoryEntry>;

/** Одна правка: к чему ставка (магазин, категория или пара) и сколько; null снимает. */
export type CommissionRateChange = {
	shopId?: string | null;
	categoryId?: string | null;
	rate: number | null;
};
export type CommissionBulkInput = {
	shopIds: string[];
	/** null — ставка магазина на все категории. */
	categoryId: string | null;
	rate: number | null;
	/** Вместе с «на всех категориях» снять персональные ставки в категориях. */
	resetPersonal?: boolean;
};

// Раздел «Интеграции»: карточка на каждый внешний сервис. Статус отвечает на
// вопрос «работает ли это сейчас», а не «заданы ли ключи»; значения секретов
// шлюз не отдаёт — только имена недостающих переменных.
export const IntegrationStatus = z.enum([
	"connected",
	"test_mode",
	"no_keys",
	"inactive",
	"disabled",
	"not_connected",
]);
export type IntegrationStatus = z.infer<typeof IntegrationStatus>;
export const IntegrationState = z.object({
	key: z.string(),
	group: z.string(),
	title: z.string(),
	provider: z.string(),
	status: IntegrationStatus,
	active: z.boolean(),
	configured: z.boolean(),
	statusNote: z.string(),
	missing: z.array(z.string()),
	details: z.array(z.object({ label: z.string(), value: z.string() })),
	impact: z.string(),
	settingsPath: z.string().optional(),
	settingsLabel: z.string().optional(),
	checkable: z.boolean(),
	lastOkAt: z.string().optional(),
	lastErrorAt: z.string().optional(),
	lastError: z.string().optional(),
	monthlyFee: z.number().nullable(),
});
export type IntegrationState = z.infer<typeof IntegrationState>;
export const IntegrationsOverview = z.object({
	environment: z.string(),
	payments: z.object({
		/** Значение PAYMENT_PROVIDER на сервере. */
		configuredProvider: z.string(),
		/** Кто реально принимает оплату. */
		activeProvider: z.string(),
		testMode: z.boolean(),
		demoTopup: z.boolean(),
		warnings: z.array(z.string()),
	}),
	items: z.array(IntegrationState),
});
export type IntegrationsOverview = z.infer<typeof IntegrationsOverview>;
export const IntegrationCheckResult = z.object({
	key: z.string(),
	ok: z.boolean(),
	message: z.string(),
	checkedAt: z.string(),
	durationMs: z.number(),
});
export type IntegrationCheckResult = z.infer<typeof IntegrationCheckResult>;
// Инфраструктура и расходы: серверы и абонплаты сервисов, руб./мес.
export const InfrastructureCosts = z.object({
	servers: z.number().nullable(),
	serversNote: z.string(),
	fees: z.record(z.string(), z.number().nullable()),
	updatedAt: z.string().nullable(),
	updatedBy: z.string().nullable(),
	totalMonthly: z.number(),
});
export type InfrastructureCosts = z.infer<typeof InfrastructureCosts>;
export type InfrastructureCostsPatch = {
	servers?: number | null;
	serversNote?: string;
	fees?: Record<string, number | null>;
};

// Правила автомодерации правок карточек. Обычная правка публикуется сразу;
// ЛЮБОЕ повышение промодерированной цены уходит заявкой (порога больше нет,
// встреча 25.09.2026). Настраивается флаг «скидка только на весь товар».
export const ModerationSettings = z.object({
	/** Устарело: порог роста цены без проверки. Не действует с 25.09.2026. */
	priceIncreasePct: z.number().optional(),
	discountWholeProductOnly: z.boolean().optional(),
});
export type ModerationSettings = z.infer<typeof ModerationSettings>;

// Заявка на повышение цены: модератор видит прежнюю (промодерированную),
// действующую и новую цену. До решения на витрине прежняя цена.
export const PriceRequestStatus = z.enum(["pending", "approved", "rejected", "cancelled"]);
export type PriceRequestStatus = z.infer<typeof PriceRequestStatus>;
export const PriceRequest = z.object({
	id: z.string(),
	productId: z.string(),
	shopId: z.string(),
	variantId: z.string().optional(),
	variantLabel: z.string(),
	approvedPrice: z.number(),
	currentPrice: z.number(),
	currentOldPrice: z.number().optional(),
	requestedPrice: z.number(),
	requestedOldPrice: z.number().optional(),
	increasePct: z.number(),
	status: PriceRequestStatus,
	sellerReason: z.string().optional(),
	decisionReason: z.string().optional(),
	moderatorId: z.string().optional(),
	createdAt: z.string(),
	decidedAt: z.string().optional(),
	productName: z.string().optional(),
	productArticle: z.string().optional(),
	productPhoto: z.string().optional(),
	shopName: z.string().optional(),
});
export type PriceRequest = z.infer<typeof PriceRequest>;
export const PriceRequestPage = z.object({ data: z.array(PriceRequest), meta: Meta });
export type PriceRequestPage = z.infer<typeof PriceRequestPage>;

// ── Settings (delivery pricing, editable) ─────────────────────────────────────
export const DeliveryConfig = z.object({
	fees: z.object({
		standard: z.number(),
		express: z.number(),
		pickup: z.number(),
	}),
	freeOver: z.number(),
	updatedAt: z.string(),
});
export type DeliveryConfig = z.infer<typeof DeliveryConfig>;

export type DeliveryConfigPatch = {
	fees?: Partial<{ standard: number; express: number; pickup: number }>;
	freeOver?: number;
};

// ── Перевозчики площадки: СДЭК, ПЭК, Деловые линии ─────────────────────────────
// Включение, статус подключения (нет ключей) и покрытие городов. Ключи живут в
// env шлюза; консоль видит только ИМЕНА недостающих переменных.
export const CarrierId = z.enum(["sdek", "pecom", "dellin"]);
export type CarrierId = z.infer<typeof CarrierId>;

export const CarrierAdmin = z.object({
	id: CarrierId,
	title: z.string(),
	enabled: z.boolean(),
	configured: z.boolean(),
	active: z.boolean(),
	status: z.enum(["active", "disabled", "no_keys"]),
	missing: z.array(z.string()),
	baseUrl: z.string(),
	capabilities: z.object({
		courier: z.boolean(),
		pickupPoints: z.boolean(),
		terminals: z.boolean(),
		heavyFreight: z.boolean(),
		labels: z.boolean(),
		webhooks: z.boolean(),
	}),
	coverage: z.object({
		note: z.string(),
		courierZones: z.array(z.object({ id: z.string(), city: z.string(), enabled: z.boolean() })),
	}),
	updatedAt: z.string().optional(),
});
export type CarrierAdmin = z.infer<typeof CarrierAdmin>;

export const CarrierCheck = z.object({
	carrier: CarrierId,
	city: z.string(),
	ok: z.boolean(),
	reason: z.enum(["no_keys", "disabled", "carrier_error"]).optional(),
	error: z.string().optional(),
	rates: z.array(
		z.object({
			carrier: CarrierId,
			serviceCode: z.string(),
			name: z.string(),
			destination: z.enum(["door", "pickup_point", "postamat", "terminal"]),
			price: z.number(),
			daysMin: z.number(),
			daysMax: z.number(),
		}),
	),
	points: z.number(),
});
export type CarrierCheck = z.infer<typeof CarrierCheck>;

// ── SMS-канал (СМС-Центр): состояние подключения и журнал отправок ───────────

export const SmsLogEntry = z.object({
	id: z.string(),
	provider: z.string(),
	providerMessageId: z.string(),
	phone: z.string(),
	purpose: z.string(),
	// Текст без кода: журнал не должен быть обходом входа по SMS.
	textPreview: z.string(),
	parts: z.number(),
	cost: z.number(),
	status: z.string(),
	providerStatus: z.number().nullable(),
	providerStatusName: z.string(),
	error: z.string(),
	errorCode: z.number().nullable(),
	sessionId: z.string().nullable(),
	createdAt: z.string(),
	updatedAt: z.string(),
});
export type SmsLogEntry = z.infer<typeof SmsLogEntry>;

export const SmsLogPage = z.object({ items: SmsLogEntry.array(), total: z.number() });
export type SmsLogPage = z.infer<typeof SmsLogPage>;

export const SmsChannelHealth = z.object({
	provider: z.string(),
	configured: z.boolean(),
	missingConfig: z.string().array(),
	sender: z.string(),
	balance: z.number().nullable(),
	credit: z.number(),
	currency: z.string(),
	balanceError: z.string(),
	balanceLow: z.boolean(),
	stats: z.object({
		windowHours: z.number(),
		sent: z.number(),
		delivered: z.number(),
		failed: z.number(),
		cost: z.number(),
	}),
});
export type SmsChannelHealth = z.infer<typeof SmsChannelHealth>;

export const SmsTestResult = z.object({
	ok: z.boolean(),
	dryRun: z.boolean(),
	messageId: z.string(),
	parts: z.number(),
	cost: z.number(),
	error: z.string(),
	errorCode: z.number().nullable(),
});
export type SmsTestResult = z.infer<typeof SmsTestResult>;

export type SmsLogFilter = { phone?: string; status?: string; limit?: number; offset?: number };

// ── Почтовый канал: статус провайдера, журнал писем, шаблоны ────────────────

export const EmailChannelStatus = z.object({
	provider: z.string(),
	live: z.boolean(),
	mode: z.enum(["live", "mock", "disabled", "not_configured"]),
	missingConfig: z.string().array(),
	configErrors: z.string().array(),
	dailyLimit: z.number(),
	sentLast24h: z.number(),
	pausedUntil: z.string().nullable(),
	pauseReason: z.string(),
	from: z.string(),
	fromName: z.string(),
	replyTo: z.string(),
	smtpHost: z.string(),
	smtpPort: z.number(),
	apiUrl: z.string(),
	webhookUrl: z.string(),
	variables: z.object({ name: z.string(), set: z.boolean(), secret: z.boolean() }).array(),
	note: z.string(),
	stats: z.object({
		windowHours: z.number(),
		total: z.number(),
		sent: z.number(),
		delivered: z.number(),
		failed: z.number(),
		queued: z.number(),
		skipped: z.number(),
	}),
});
export type EmailChannelStatus = z.infer<typeof EmailChannelStatus>;

export const EmailLogEntry = z.object({
	id: z.string(),
	template: z.string(),
	templateTitle: z.string(),
	category: z.string(),
	purpose: z.string(),
	to: z.string(),
	recipientUserId: z.string().nullable(),
	subject: z.string(),
	status: z.string(),
	statusReason: z.string(),
	provider: z.string(),
	providerMessageId: z.string(),
	providerStatus: z.string(),
	attempts: z.number(),
	maxAttempts: z.number(),
	nextAttemptAt: z.string().nullable(),
	lastError: z.string(),
	createdAt: z.string(),
	updatedAt: z.string(),
	sentAt: z.string().nullable(),
	deliveredAt: z.string().nullable(),
});
export type EmailLogEntry = z.infer<typeof EmailLogEntry>;

export const EmailLogPage = z.object({ items: EmailLogEntry.array(), total: z.number() });
export type EmailLogPage = z.infer<typeof EmailLogPage>;

export const EmailMessageDetail = EmailLogEntry.extend({ html: z.string(), text: z.string() });
export type EmailMessageDetail = z.infer<typeof EmailMessageDetail>;

export const EmailTemplateMeta = z.object({
	id: z.string(),
	audience: z.enum(["seller", "buyer", "admin"]),
	category: z.enum(["critical", "service", "marketing"]),
	title: z.string(),
	sensitive: z.boolean(),
});
export type EmailTemplateMeta = z.infer<typeof EmailTemplateMeta>;

export const EmailTemplatePreview = EmailTemplateMeta.extend({
	subject: z.string(),
	html: z.string(),
	text: z.string(),
});
export type EmailTemplatePreview = z.infer<typeof EmailTemplatePreview>;

export const EmailSendOutcome = z.object({
	ok: z.boolean(),
	id: z.string().nullable(),
	status: z.string(),
	error: z.string(),
});
export type EmailSendOutcome = z.infer<typeof EmailSendOutcome>;

export type EmailLogFilter = { to?: string; status?: string; template?: string; limit?: number; offset?: number };

// ── Geo settings (supported markets + currencies, editable) ───────────────────
// The single source of truth the storefront location picker and the phone
// country lists read. The admin surface returns the full list incl. disabled.
export const GeoCountry = z.object({
	code: z.string(),
	name: z.string(),
	currency: z.string(),
	cities: z.array(z.string()),
	enabled: z.boolean(),
});
export type GeoCountry = z.infer<typeof GeoCountry>;

export const GeoCurrency = z.object({
	code: z.string(),
	symbol: z.string(),
	name: z.string(),
	rate: z.number(),
	symbolBefore: z.boolean(),
});
export type GeoCurrency = z.infer<typeof GeoCurrency>;

// ---------------------------------------------------------------------------
// Founder workspace (mock-only — see the `dashboard`/`finances` note on `Api`)
// ---------------------------------------------------------------------------

/**
 * Quick presets, in days. Elina asked for 1/3/7 "и дальше любые даты"; 14 is
 * the seller settlement period («расчётный период — две недели»), so it earns a
 * preset of its own rather than living only as a custom range.
 */
export const DASHBOARD_PRESETS = [1, 3, 7, 14, 30, 90] as const;
export type DashboardPreset = (typeof DASHBOARD_PRESETS)[number];

/**
 * First day statistics were collected. "С первого дня" measures from here, so
 * the founder never has to remember when the platform opened — «мне не надо
 * будет сидеть и думать, какой день мы начали работу».
 *
 * TODO: move to platform settings once the gateway exposes it.
 */
export const PLATFORM_START_DATE = "2026-01-01";

/**
 * Either a preset window, everything since launch, or an arbitrary one. Custom
 * periods were an explicit request («год, 9 месяцев, 34 дня, 47 дней»), so the
 * range cannot stay a string union. `from`/`to` are ISO days, inclusive.
 *
 * `allTime` is the default on every dashboard: until the marketplace is at
 * volume the founder reads the whole picture, not a slice — «раз я нажала
 * всего, я вижу, у нас на площадке 50 тысяч пользователей».
 */
export type DashboardRange =
	| { kind: "allTime" }
	| { kind: "preset"; days: DashboardPreset }
	| { kind: "custom"; from: string; to: string };

/** Severity used by stat rows and chips — what "burns red" on screen. */
export type StatTone = "default" | "warning" | "danger";

/**
 * One labelled number. `key` resolves to an i18n label; `note` carries a
 * derived secondary figure (e.g. "за сутки не обработано").
 */
export type StatRow = {
	key: string;
	value: number;
	note?: number;
	tone?: StatTone;
	/**
	 * Ready-made label, used when the row names a person or shop rather than a
	 * fixed category — those have no i18n key to resolve.
	 */
	label?: string;
};

/**
 * A group of stat rows. Rendered as text by default and as a bar chart when the
 * dashboard is switched to chart view — same data, two presentations.
 */
export type StatBlock = {
	key: string;
	rows: StatRow[];
	/** Admin route this block drills into. */
	href?: string;
	/** Marks a block the founder called "second wave" (attribution). */
	secondary?: boolean;
	/**
	 * i18n key for `StatRow.note`, when this block's secondary figure is not the
	 * default "unprocessed in 24h" alarm. Manager rows carry a mean handling
	 * time, which is information, not a warning — it must not render red.
	 */
	noteKey?: string;
	/** Tone for the note text. Defaults to `danger` to preserve the alarm case. */
	noteTone?: StatTone;
};

/** One point on a time series. `date` is an ISO day (YYYY-MM-DD). */
export type SeriesPoint = { date: string; value: number };

/** A headline number with its change vs the preceding window of equal length. */
export type Metric = {
	value: number;
	/** Fractional change vs previous period: 0.12 = +12%. Absent when no baseline. */
	delta?: number;
};

/** A dashboard card: headline metric, its series, and where drilling down goes. */
export type DashboardCard = {
	key: string;
	metric: Metric;
	series: SeriesPoint[];
	/** Admin route the card drills into (decision 2A — reuse existing pages). */
	href: string;
	/**
	 * Cumulative all-time count, shown as minor text under the headline. The
	 * headline is period-scoped ("150 registrations in 30 days"), which is
	 * meaningless without the base it grew from. Only set on cards that count
	 * a population; a rate or an average has no total.
	 */
	total?: number;
};

export type FunnelStage = { key: string; count: number };

/**
 * A ranked "top N" entry — the seller-panel analytics pattern: position, what
 * it is, the number that ranks it, and the reason behind it.
 */
export type RankedItem = {
	key: string;
	label: string;
	/** Article, category, city — whatever identifies the row underneath. */
	sublabel?: string;
	value: number;
	/** Dominant cause, e.g. the most common return reason. */
	reason?: string;
	tone?: StatTone;
	href?: string;
};

export type RankedList = {
	key: string;
	items: RankedItem[];
	/** i18n key for the unit shown after each value. */
	unitKey?: string;
};

/** One tab's worth of the dashboard. */
export type DashboardSection = {
	cards: DashboardCard[];
	blocks: StatBlock[];
	/** Top-N leaderboards, rendered below the blocks. */
	lists?: RankedList[];
};

/** Applications counters mirrored onto the dashboard (the «сноски»). */
export type ApplicationsSummary = {
	statuses: { status: ApplicationStatus; count: number }[];
	/** Cards needing attention, by severity. */
	alerts: { tone: Exclude<StatTone, "default">; count: number }[];
};

export type DashboardOverview = {
	range: DashboardRange;
	/** Seller-side: registrations, GMV, orders, onboarding funnel, catalog. */
	seller: DashboardSection;
	/** Buyer-side: registrations, activity, app usage, basket behaviour. */
	buyer: DashboardSection;
	/** Returns and fulfilment quality — where the platform is failing. */
	quality: DashboardSection;
	/** Manager workload and overdue work. Applications are one of their duties. */
	managers: DashboardSection;
	applications: ApplicationsSummary;
};

export type FinanceBreakdownRow = {
	key: string;
	label: string;
	gmv: number;
	commission: number;
	orders: number;
	// Только для строк «по магазинам»: slug витрины для перехода в карточку.
	slug?: string;
};

export type PayoutStatus = "pending" | "processing" | "paid" | "failed";

export type Payout = {
	id: string;
	shopId: string;
	shopName: string;
	amount: number;
	status: PayoutStatus;
	period: string;
	createdAt: string;
};

/**
 * Scheduled seller settlement. Payouts run twice a month — «каждое 15 и первое
 * число мы рассчитываемся с нашими продавцами» — so the founder needs to see
 * what is accruing towards the next date for planning.
 */
export type PayoutSchedule = {
	/**
	 * Ближайшая дата открытия окна выплаты по периоду с долгом — по графику
	 * выплат продавцам (за 1-15 число — с 25-го, за 16-конец — с 10-го
	 * следующего месяца; services/payoutSchedule.ts в шлюзе).
	 */
	nextDate: string;
	/** Станет к выплате в эту дату. */
	amountDue: number;
	sellersCount: number;
	/** Период, за который открывается окно: «1-15 октября 2026». */
	periodKey?: string;
	periodLabel?: string;
	/** Уже к выплате сейчас (окна открыты). */
	availableNow?: number;
	/** Previous settlements, most recent first. */
	history: { date: string; amount: number }[];
};

/**
 * Platform P&L for the period. Advertising is modelled from the start on the
 * founder's instruction — «стоит вот эту рекламную историю сразу занести,
 * чтобы потом не впиливать её никуда».
 */
export type FinancePnl = {
	gmv: number;
	commission: number;
	/** Promotion and ad-cabinet revenue. INVENTED — no ad product exists yet. */
	advertising: number;
	/** commission + advertising. */
	grossEarnings: number;
	/** Acquiring, payouts handling, support — INVENTED. */
	expenses: number;
	netRevenue: number;
};

/**
 * How often a counterparty settles. Sellers are on the platform's fortnightly
 * cycle; carriers dictate their own — «СДЭК может сказать: я хочу раз в день.
 * Ты не можешь им сказать нет».
 */
export type SettlementCadence = "daily" | "weekly" | "fortnightly" | "half_monthly" | "monthly";

/** Заявка продавца на выплату, связанная с обязательством того же периода. */
export type ObligationRequest = {
	id: string;
	amount: number;
	status: "new" | "approved" | "paid";
	paidAt?: string;
};

/** One counterparty's running balance: what accrued, what was paid, what is due. */
export const OBLIGATION_STATES = ["formed", "identified", "documented", "paid", "settled"] as const;

export type ObligationState = (typeof OBLIGATION_STATES)[number];

export type FinanceObligation = {
	key: string;
	label: string;
	kind: "seller" | "carrier";
	/** Accrued in the period — the credit side. */
	accrued: number;
	/** Already transferred — the debit side. */
	paid: number;
	/** Outstanding, payable on `nextDate`. */
	due: number;
	nextDate: string;
	cadence: SettlementCadence;
	state: ObligationState;
	/** Расчётный период обязательства (сетка выплат), YYYY-MM-DD. */
	periodFrom?: string;
	periodTo?: string;
	counterpartyId?: string;
	/** Ключ периода графика выплат: YYYY-MM-1 / YYYY-MM-2 (до октября 2026 — YYYY-MM). */
	periodKey?: string;
	periodLabel?: string;
	/** С этого момента продавец может запросить выплату за период, ISO. */
	opensAt?: string;
	/** Окно выплаты открыто: остаток к выплате, а не заблокирован. */
	payoutOpen?: boolean;
	/** В заявках продавца, которые ещё не выплачены. */
	pending?: number;
	/** Заявки продавца за этот период: выплаченные закрывают обязательство. */
	requests?: ObligationRequest[];
	documents: ObligationDocument[];
};

/**
 * Документ обязательства — ссылка на выгрузку «Бухгалтерии» (отчёт агента или
 * акт вознаграждения за месяц по магазину); файл собирается при скачивании.
 * Записи до 26.09.2026 — пустые «settlement-act» без месяца.
 */
export type ObligationDocument = {
	id: string;
	kind: "agent-report" | "commission-act" | string;
	createdAt: string;
	period?: string;
	shopId?: string;
};

/**
 * EXTERNAL CONTOUR — money that merely passes THROUGH the platform on its way
 * to sellers and carriers. None of it is the platform's own.
 *
 * Splitting this from the treasury was the founder's central finance ask:
 * «здесь я вижу внешние показатели, перемешанные с внутренними. Давайте его
 * просто на два разобьём».
 */
export type FinanceExternal = {
	gmv: number;
	/** Accrued to sellers over the period. */
	accrued: number;
	/**
	 * Accrued but still locked because the settlement period has not closed —
	 * «дивиденды остались дивидендами, просто пока на нашем балансе
	 * заблокированы».
	 */
	blocked: number;
	/** Payable on the next settlement date. */
	available: number;
	/** Already transferred within the period. */
	paid: number;
	/** Из начисленного — в заявках продавцов, которые ещё не выплачены. */
	pending?: number;
	/** Архив старой 14-дневной сетки: записей и сумма учётных отметок «Оплачено». */
	legacyArchive?: { obligations: number; paidMarked: number };
	/**
	 * Delivery is transit money, not revenue: withheld from sellers and owed
	 * onward to carriers — «доставку мы с них берём, другим отдаём».
	 */
	delivery: { withheld: number; payable: number; paid: number };
	obligations: FinanceObligation[];
};

/**
 * INTERNAL CONTOUR — what the platform earns on its own account.
 *
 * Costs are out of scope by decision: no one keys them in and no integration
 * supplies them, so any expense line here would be invented. They stay in
 * accounting until there is a real source.
 */
export type FinanceInternal = {
	commission: number;
	advertising: number;
	/** commission + advertising — everything the platform actually earned. */
	income: number;
};

export type FinanceSummary = {
	range: DashboardRange;
	currency: string;
	/** Settlements with sellers and carriers — transit money. */
	external: FinanceExternal;
	/** The platform's own earnings and costs. */
	internal: FinanceInternal;
	gmv: Metric;
	/** Platform take. INVENTED — no commission model exists in the backend. */
	commission: Metric;
	netRevenue: Metric;
	aov: Metric;
	refunds: Metric;
	/** Ad revenue for the period. INVENTED. */
	advertising: Metric;
	pnl: FinancePnl;
	schedule: PayoutSchedule;
	gmvSeries: SeriesPoint[];
	commissionSeries: SeriesPoint[];
	byCategory: FinanceBreakdownRow[];
	byShop: FinanceBreakdownRow[];
	/** Payout queue. INVENTED — there is no payouts table. */
	payouts: Payout[];
};

// ---------------------------------------------------------------------------
// Seller onboarding applications — kanban (mock-only)
// ---------------------------------------------------------------------------

/** Board columns, in pipeline order. Mirrors the seller onboarding lifecycle. */
/**
 * Стадии воронки подключения продавца.
 *
 * Описывают работу площадки с бизнесом, а не экраны, которые он заполняет:
 * пришёл с лендинга → менеджер связался → завёл аккаунт → заполняет кабинет →
 * проверка → запуск. Порядок задаёт колонки доски и сортировку по стадии.
 *
 * `rejected` (отказала площадка) и `lost` (бизнес не дошёл сам) разведены:
 * первое мерит качество проверки, второе даёт базу для повторного обзвона.
 */
export const APPLICATION_STATUSES = [
	"new",
	"contacted",
	"registered",
	"filling",
	"moderation",
	// «На доработке» (fd-onboard-18): площадка нашла, что исправить, и вернула
	// заявку продавцу. Это НЕ отказ — после исправления заявка возвращается на
	// модерацию, поэтому стадия стоит между `moderation` и финалом и продолжает
	// копить дни (не входит в CLOSED_STATUSES).
	"rework",
	"active",
	"rejected",
	"lost",
] as const;
export type ApplicationStatus = (typeof APPLICATION_STATUSES)[number];

/** A platform manager who can own an application. */
export type ApplicationAssignee = { id: string; name: string; initials: string };

export type ApplicationEvent = {
	id: string;
	at: string;
	/** Human-readable line; `automated` marks system-generated entries. */
	message: string;
	automated: boolean;
};

/** Уровень приоритета заявки по SLA — считает шлюз. */
export type ApplicationPriorityLevel = "low" | "normal" | "high" | "critical";
export type ApplicationAlertSeverity = "error" | "warning";

/**
 * Сроки заявки с сервера: дни в стадии (календарные, по Москве), просрочка,
 * «осталось N дней», приоритет и предупреждения доски. Консоль их только
 * показывает — своих часов и порогов у неё больше нет.
 */
export type ApplicationSla = {
	daysInStage: number;
	slaDays: number;
	overdue: boolean;
	daysLeft: number | null;
	priority: ApplicationPriorityLevel;
	alerts: { kind: "stale" | "unassigned"; severity: ApplicationAlertSeverity }[];
	severity: ApplicationAlertSeverity | null;
};

export type SellerApplication = {
	id: string;
	shopName: string;
	contactName: string;
	city: string;
	categories: string[];
	status: ApplicationStatus;
	assignee: ApplicationAssignee | null;
	submittedAt: string;
	/** When the card last changed column — drives the "stuck for N days" badge. */
	statusChangedAt: string;
	priority: "low" | "normal" | "high";
	/** Сроки, приоритет и предупреждения — считает шлюз. */
	sla: ApplicationSla;
	note?: string;
	history: ApplicationEvent[];
	/** Телефон обращения — по нему менеджер звонит и ищутся дубли. */
	phone?: string;
	email?: string;
	/** ИНН из короткой заявки: приходит раньше аккаунта. */
	inn?: string;
	/** Аккаунт продавца, если он уже есть. Пусто — кабинета ещё нет. */
	userId?: string;
} & CrmBoardExtras;

/** Карточка заявки CRM: строка доски + контакты, лента, задачи, файлы. */
export type ApplicationCard = CrmLeadCard<SellerApplication>;

/** Результат создания кабинета менеджером: кому и куда ушло приглашение. */
export type ApplicationAccountResult = {
	applicationId: string;
	userId: string;
	email: string;
	inviteUrl: string;
	inviteExpiresAt: string;
	smsSent: boolean;
	smsError?: string;
};

export type ApplicationAccountInput = {
	email: string;
	shopName?: string;
	contactName?: string;
	inn?: string;
};

export type ApplicationBoard = {
	applications: SellerApplication[];
	assignees: ApplicationAssignee[];
};

/**
 * Письменное замечание модератора к заявке (fd-onboard-19). `docType`
 * привязывает его к конкретной секции «личного дела» (посекционная
 * ре-модерация); `null` — замечание ко всей заявке. `resolvedAt` проставляется
 * повторной отправкой продавцом. Форма поля в поле повторяет api-gateway.
 */
export type ApplicationRemark = {
	id: string;
	applicationId: string;
	docType: string | null;
	message: string;
	deadline: string | null;
	createdAt: string;
	resolvedAt: string | null;
};

/**
 * Тело отправки заявки на доработку (POST /admin/applications/:id/rework):
 * обязательный текст замечания, необязательный срок (ISO-день) и необязательная
 * привязка к секции «личного дела».
 */
export type ApplicationReworkInput = {
	message: string;
	deadline?: string;
	docType?: SellerDocumentType;
};

/** Ответ отправки на доработку: обновлённая заявка и созданное замечание. */
export type ApplicationReworkResult = {
	application: SellerApplication;
	remark: ApplicationRemark;
};

// Analytics reports. The catalog lists the report types the platform can build;
// `ai:true` ones run an LLM pass over the numbers and take 5–15s. `generate`
// returns a self-contained HTML document (its own <style>), shown in an iframe
// and reprintable to PDF; the history lists the last 50 generated reports.
export const ReportCatalogEntry = z.object({
	type: z.string(),
	title: z.string(),
	description: z.string(),
	scope: z.string(),
	ai: z.boolean(),
	/** lucide-react icon name, e.g. "TrendingUp". */
	icon: z.string(),
});
export type ReportCatalogEntry = z.infer<typeof ReportCatalogEntry>;

export const GeneratedReport = z.object({
	id: z.string(),
	title: z.string(),
	aiUsed: z.boolean(),
	html: z.string(),
});
export type GeneratedReport = z.infer<typeof GeneratedReport>;

export const ReportHistoryEntry = z.object({
	id: z.string(),
	type: z.string(),
	title: z.string(),
	aiUsed: z.boolean(),
	createdAt: z.string(),
});
export type ReportHistoryEntry = z.infer<typeof ReportHistoryEntry>;

export type BuyerOrderRow = {
	id: string;
	/** Короткий номер заказа (миграция 285). */
	number?: number;
	status: string;
	total: number;
	itemCount: number;
	createdAt: string;
	shops: string[];
	thumbnails: string[];
};

export type BuyerAnalytics = {
	userId: string;
	activity: "active" | "dormant" | "new";
	ordersTotal: number;
	ordersPaid: number;
	ordersCancelled: number;
	gmv: number;
	avgCheck: number;
	firstOrderAt?: string;
	lastOrderAt?: string;
	daysSinceLastOrder?: number;
	topShops: { shopName: string; orders: number }[];
	orders: BuyerOrderRow[];
};

// Рейтинг покупателя в топе площадки: по сумме трат (spend) либо по числу
// заказов (orders). DTO — сервисный тип api-gateway/buyerAnalytics (не zod в
// contracts), поэтому объявляем его здесь тем же shape.
export type TopBuyerMetric = "spend" | "orders";

export type TopBuyer = {
	userId: string;
	name?: string;
	email?: string;
	phone?: string;
	orders: number;
	gmv: number;
	avgCheck: number;
	lastOrderAt?: string;
};

// «Скидка OSMIX» и партнёрские промокоды: один контракт со шлюзом
// (packages/contracts) — ручная копия отставала бы на каждом новом поле.
import type {
	Partner,
	PartnerInput,
	PartnerPatch,
	PartnerSettings,
	PartnerStats,
	PromoCheckResult,
	PromoCode as ContractPromoCode,
	PromoCodeInput as ContractPromoCodeInput,
	PromoStats,
	SellerPartnerPromo,
	PersonalOffersSettings,
	PersonalOffersRunResult,
} from "@osmix/contracts";
export type {
	Partner,
	PartnerInput,
	PartnerKind,
	PartnerPatch,
	PartnerSettings,
	PartnerStats,
	PromoAudience,
	PromoCheckResult,
	PromoOrderStatusGroup,
	PromoRewardKind,
	PromoState,
	PromoStats,
	PromoStatsTotals,
	PromoSummary,
	SellerPartnerPromo,
} from "@osmix/contracts";
export type PromoKind = "percent" | "fixed" | "free_delivery";
export type { PersonalOffersSettings, PersonalOffersRunResult, PersonalOfferRule, PersonalOfferRuleKey } from "@osmix/contracts";
/** Вкладка «Персональные» (fd-promo-12): правила, последний прогон, выданные коды. */
export type PersonalOffersOverview = {
	settings: PersonalOffersSettings;
	lastRun: PersonalOffersRunResult | null;
	codes: PromoCode[];
};
export type PromoCode = ContractPromoCode;
export type PromoCodeInput = ContractPromoCodeInput;
export type PromoPeriod = { from?: string; to?: string };
export type PartnerDetail = { partner: Partner; promos: PromoCode[]; stats: PartnerStats };


// ── Журнал действий консоли (миграция 034 + 082) ────────────────────────────
// Пишется перехватчиком на шлюзе: каждое изменяющее обращение к /admin/* плюс
// чувствительные чтения (документы продавца). Читается разделом «Журнал» под
// правом action.audit.view.
export type AuditEntry = {
	id: string;
	actorUserId?: string;
	actorName: string;
	actorEmail?: string;
	entityKind: string;
	entityId: string;
	action: string;
	fromStatus?: string;
	toStatus?: string;
	reason?: string;
	metadata: Record<string, unknown>;
	method?: string;
	path?: string;
	/** Шаблон маршрута: по нему группируются однотипные действия. */
	route?: string;
	statusCode?: number;
	ip?: string;
	userAgent?: string;
	durationMs?: number;
	createdAt: string;
};

export type AuditPage = {
	items: AuditEntry[];
	total: number;
	page: number;
	pageSize: number;
};

export type AuditFacets = {
	kinds: { value: string; count: number }[];
	actions: { value: string; count: number }[];
	actors: { id: string; name: string; count: number }[];
};

export type AuditFilter = {
	actorUserId?: string;
	entityKind?: string;
	entityId?: string;
	action?: string;
	q?: string;
	from?: string;
	to?: string;
	page?: number;
	pageSize?: number;
};


// Журнал обращений к внешним системам (миграция 083): вебхуки CloudPayments и
// СДЭК и наши исходящие вызовы к ним. Полезная нагрузка приходит очищенной —
// токенов и криптограмм в ней нет.
export type IntegrationEvent = {
	id: string;
	provider: string;
	direction: "in" | "out";
	event: string;
	status: "ok" | "error" | "ignored";
	refKind: string;
	refId: string;
	httpStatus?: number;
	durationMs?: number;
	payload: Record<string, unknown>;
	error?: string;
	createdAt: string;
};

export type IntegrationEventSummary = {
	provider: string;
	total: number;
	errors: number;
	lastAt?: string;
};

export type IntegrationEventFilter = {
	provider?: string;
	status?: string;
	refId?: string;
	limit?: number;
};

export type Api = {
	// Акции площадки и заявки продавцов на участие (П3, ./promo-campaigns.ts).
	promoCampaigns: import("./promo-campaigns").PromoCampaignsApi;
	// Цифровые UGC-артикулы: реестр консоли (ТЗ 31.10.2026, ./ugc-articles.ts).
	ugc: import("./ugc-articles").UgcAdminApi;
	auth: {
		login(credentials: Credentials): Promise<AuthSession>;
		me(): Promise<AuthUser>;
		logout(): Promise<void>;
	};
	products: {
		list(filter: ProductFilter): Promise<ProductPage>;
		/** Сколько карточек в каждом статусе (в текущем срезе): ключи статусов + `all`. */
		statusCounts(query?: ProductQuery): Promise<ProductStatusCounts>;
		/** id карточек под фильтром — пусто, если их больше потолка массового действия. */
		ids(query: ProductQuery): Promise<ProductIds>;
		/** Справочники фильтров: магазины, категории, тимлиды. */
		filterOptions(): Promise<ProductFilterOptions>;
		get(id: string): Promise<Product>;
		approve(id: string): Promise<Product>;
		rejectionReasons(): Promise<RejectionReason[]>;
		reject(id: string, input?: ProductRejectInput): Promise<Product>;
		/** Снять с витрины площадкой (с причиной); снимает блокировку approve. */
		block(id: string, reason: string): Promise<Product>;
		bulk(input: ProductBulkInput): Promise<ProductBulkResult>;
		/** Поправка раздела/пола у товара; null снимает поправку. */
		setFacets(id: string, input: CategoryFacetsInput): Promise<void>;
		/**
		 * Перенос карточки в другую категорию (конечный неархивный узел) —
		 * быстрый выход для товара из архивной категории.
		 * PUT /admin/products/:id/category — на шлюзе ещё нет, см. отчёт.
		 */
		moveCategory(id: string, categoryId: string): Promise<void>;
		/** Заявки на повышение цены (по умолчанию — ждущие решения). */
		priceRequests(filter: {
			status?: PriceRequestStatus | "all";
			page: number;
			pageSize: number;
		}): Promise<PriceRequestPage>;
		approvePriceRequest(id: string): Promise<PriceRequest>;
		rejectPriceRequest(id: string, reason: string): Promise<PriceRequest>;
	};
	users: {
		buyerAnalytics(id: string): Promise<BuyerAnalytics>;
		// Топ-покупатели площадки: рейтинг по сумме трат или числу заказов за
		// окно (GET /admin/buyers/top?metric=&days=&limit=).
		topBuyers(input: { metric: TopBuyerMetric; days?: number; limit?: number }): Promise<{ items: TopBuyer[] }>;
		list(): Promise<User[]>;
		/** Страница пользователей: поиск и фильтр по роли — на сервере. */
		page(filter: UserPageFilter): Promise<ServerPage<User>>;
		get(id: string): Promise<User>;
		create(input: UserCreateInput): Promise<User>;
		update(id: string, patch: UserPatch): Promise<User>;
		changePassword(id: string, input: UserPasswordInput): Promise<User>;
		block(id: string, reason: string): Promise<User>;
		unblock(id: string): Promise<User>;
		staff(id: string): Promise<ShopMember[]>;
	};
	notifications: {
		list(input: { cursor?: string; limit?: number; filter?: "all" | "unread"; locale?: NotificationLocale }): Promise<NotificationPage>;
		detail(id: string, locale?: NotificationLocale): Promise<NotificationItem>;
		unreadCount(): Promise<{ count: number }>;
		markRead(id: string): Promise<void>;
		markAllRead(): Promise<void>;
	};
	notificationCampaigns: {
		list(input?: { cursor?: string; limit?: number; status?: NotificationCampaign["status"] }): Promise<NotificationCampaignPage>;
		get(id: string): Promise<NotificationCampaign>;
		create(input: NotificationCampaignDraftRequest): Promise<NotificationCampaign>;
		update(id: string, input: NotificationCampaignUpdateRequest): Promise<NotificationCampaign>;
		preview(id: string, locale?: NotificationLocale): Promise<NotificationCampaignPreview>;
		send(id: string, input: NotificationCampaignSendRequest): Promise<NotificationCampaign>;
		cancel(id: string): Promise<NotificationCampaign>;
		// Статистика доставки/прочтений кампании: recipients / delivered / read /
		// readRate + разбивка по дням (GET /admin/notification-campaigns/:id/stats).
		stats(id: string): Promise<NotificationCampaignStats>;
		recipients(input?: { cursor?: string; limit?: number; search?: string }): Promise<NotificationRecipientPage>;
		// «Похожие подборки недели» (fd-notif-01): включение рассылки и каденция
		// (GET/PUT /admin/notifications/weekly-digest).
		weeklyDigest(): Promise<WeeklyDigestSettings>;
		updateWeeklyDigest(input: WeeklyDigestSettings): Promise<WeeklyDigestSettings>;
	};
	platformRoles: {
		catalog(): Promise<PlatformPermission[]>;
		list(): Promise<PlatformRole[]>;
		create(input: PlatformRoleInput): Promise<PlatformRole>;
		update(key: string, patch: PlatformRolePatch): Promise<PlatformRole>;
		remove(key: string): Promise<void>;
	};
	/** Документы о соответствии (17.10.2026). */
	compliance: {
		summary(): Promise<ComplianceAdminSummary>;
		/** «Снять с продажи все без документа» - действие администратора. */
		unpublishMissing(): Promise<{ count: number; shops: number }>;
		/** «Применить рекомендации» матрицы на незаданных узлах. */
		applyRecommended(): Promise<{ updated: string[] }>;
		/** Узел дерева: флажок, виды, основание; null - как у родителя. */
		setCategory(id: string, input: CategoryComplianceInput): Promise<CategoryCompliance>;
		rejectDocument(id: string, reason: string): Promise<ComplianceDocument>;
		documentFile(id: string): Promise<Blob>;
		/** Очередь проверки документов (31.10.2026). */
		listDocuments(query: ComplianceQueueQuery): Promise<ComplianceQueuePage>;
		getDocument(id: string): Promise<ComplianceDocument>;
		documentProducts(id: string): Promise<ComplianceDocumentProduct[]>;
		approveDocument(id: string, input: { validUntil?: string | null; comment?: string }): Promise<ComplianceDocument>;
		/** «Сертификат на партию подтверждён площадкой» на товаре. */
		admit(productId: string, input: ComplianceAdmissionInput): Promise<ProductCompliance>;
		revokeAdmission(productId: string, reason?: string): Promise<ProductCompliance>;
		report(query?: ComplianceReportQuery): Promise<ComplianceReport>;
		reportCsv(query?: ComplianceReportQuery): Promise<{ blob: Blob; filename: string }>;
		journal(query?: { page?: number; pageSize?: number; entityId?: string }): Promise<ComplianceJournalPage>;
		matrix(): Promise<ComplianceMatrixRow[]>;
	};
	categories: {
		list(): Promise<Category[]>;
		create(input: CategoryInput): Promise<Category>;
		update(id: string, patch: Partial<CategoryInput>): Promise<Category>;
		/** moveTo — куда перенести товары удаляемой категории. */
		remove(id: string, moveTo?: string): Promise<void>;
		deleteImpact(id: string): Promise<CategoryDeleteImpact>;
		setFacets(id: string, input: CategoryFacetsInput): Promise<void>;
		/** Схема характеристик самого узла (без наследования). */
		attributeSchema(id: string): Promise<CategoryAttributeSchema>;
		/** Своя схема характеристик узла целиком (заменяет прежнюю). */
		updateAttributeSchema(id: string, attributes: CategoryAttributeDefinition[]): Promise<CategoryAttributeSchema>;
		/** «Сбросить к наследованию»: своя схема узла удаляется, действует схема ближайшего предка. */
		resetAttributeSchema(id: string): Promise<void>;
		/** Сколько опубликованных товаров попадает в каждый раздел витрины. */
		sectionCounts(): Promise<{ section: string; count: number }[]>;
	};
	inspirationFeed: {
		list(): Promise<InspirationFeedItem[]>;
		add(input: InspirationFeedItemInput): Promise<InspirationFeedItem>;
		update(
			id: string,
			patch: Partial<InspirationFeedItemInput>,
		): Promise<InspirationFeedItem>;
		reorder(ids: string[]): Promise<InspirationFeedItem[]>;
		remove(id: string): Promise<void>;
	};
	inspirationIdeas: {
		list(): Promise<InspirationIdeaFeedItem[]>;
		reorder(ids: string[]): Promise<InspirationIdeaFeedItem[]>;
	};
	// Единая управляемая выдача главной (fd-feed-01, gateway-backed). Оператор
	// ведёт упорядоченный список типизированных блоков одного плейсмента; оба
	// клиента (веб/мобилка) рендерят один и тот же контракт. `reorder` целиком
	// задаёт порядок блоков плейсмента и возвращает обновлённый список.
	feed: {
		list(placement: FeedPlacement): Promise<FeedBlockAdmin[]>;
		create(input: CreateFeedBlockRequest): Promise<FeedBlockAdmin>;
		update(id: string, input: UpdateFeedBlockRequest): Promise<FeedBlockAdmin>;
		remove(id: string): Promise<void>;
		reorder(placement: FeedPlacement, orderedIds: string[]): Promise<FeedBlockAdmin[]>;
		// Загрузка своего изображения для слайда/плитки блока: сырой бинарный upload
		// (POST /admin/feed/media), возвращает публичный URL для imageUrl.
		uploadMedia(file: File): Promise<string>;
		// Персональная лента товаров (п.16 правок 27.10): переключатель и веса
		// сигналов (GET/PUT /admin/feed/personal-settings).
		personalSettings(): Promise<PersonalFeedSettings>;
		updatePersonalSettings(patch: PersonalFeedSettingsPatch): Promise<PersonalFeedSettings>;
	};
	// Seller-authored ideas: moderation (approve/reject) + feed curation. `curate`
	// replaces the whole featured set with the given ids, in that order (only
	// published ideas are accepted).
	ideas: {
		list(filter: IdeaFilter): Promise<Idea[]>;
		/** Страница списка идей консоли: срез, магазин, порядок — на сервере. */
		page(filter: IdeaPageFilter): Promise<ServerPage<Idea>>;
		/** Счётчики вкладок по всем идеям (в пределах магазина). */
		counts(shopId?: string): Promise<Record<IdeaListSlice, number>>;
		/** Магазины, у которых есть идеи. */
		shops(): Promise<{ id: string; name: string }[]>;
		approve(id: string, note?: string): Promise<Idea>;
		reject(id: string, reason?: string): Promise<Idea>;
		curate(ids: string[]): Promise<Idea[]>;
		// Создание идеи-вдохновения самой площадкой (автор OSMIX) — дополнение к
		// модерации идей продавцов (POST /admin/ideas).
		createPlatform(input: CreatePlatformIdeaInput): Promise<Idea>;
		// Редактор консоли: одна идея, правка любой (своей и продавца), блокировка
		// и удаление. approve/reject/block принимают примечание модератора.
		get(id: string): Promise<Idea>;
		update(id: string, input: Partial<CreatePlatformIdeaInput>): Promise<Idea>;
		block(id: string, note?: string): Promise<Idea>;
		remove(ids: string[]): Promise<void>;
		// Загрузка фото для идеи площадки: сырой бинарный upload (POST
		// /admin/ideas/photo), возвращает публичный URL изображения.
		uploadPhoto(file: File): Promise<string>;
	};
	orders: {
		list(): Promise<Order[]>;
		/** Страница заказов консоли: фильтр, поиск и сортировка на сервере. */
		page(filter: OrderPageFilter): Promise<ServerPage<Order>>;
		/** Заказов в каждом статусе при текущем поиске и датах — для чипов групп. */
		statusCounts(query: OrderQuery): Promise<Record<string, number>>;
		/** Магазины, встречающиеся в заказах, — для фильтра «Магазин». */
		shops(): Promise<{ id: string; name: string }[]>;
		shipments(orderId: string): Promise<OrderShipment[]>;
		// Подтверждение оплаты по счёту (заказ ЮЛ, paymentMethod=invoice).
		confirmInvoicePayment(orderId: string): Promise<Order>;
		// Ручная смена статуса площадкой. Отмена возвращает деньги покупателю
		// (карта — через PSP, баланс — на баланс) и остатки на склад.
		setStatus(orderId: string, input: { status: AdminOrderTargetStatus; reason?: string }): Promise<Order>;
		// Состав чека по заказу.
		receipt(orderId: string): Promise<OrderReceipt>;
	};
	// Возвраты (fd-order-19..22). Администратор разбирает все заявки: решает
	// (одобрить/отклонить с флагом обоснованности), отмечает получение товара
	// и факт возврата денег.
	returns: {
		list(status?: ReturnStatus): Promise<ReturnRequest[]>;
		/** Страница заявок: статус, поиск и сортировка — на сервере. */
		page(filter: ReturnPageFilter): Promise<ServerPage<ReturnRequest>>;
		summary(): Promise<ReturnsSummary>;
		resolve(id: string, input: ReturnResolveInput): Promise<ReturnRequest>;
		received(id: string): Promise<ReturnRequest>;
		refunded(id: string): Promise<ReturnRequest>;
		get(id: string): Promise<ReturnDetail>;
		// Решение площадки по спору покупателя и продавца.
		resolveDispute(id: string, input: { outcome: DisputeOutcome; note?: string }): Promise<ReturnRequest>;
	};
	carts: {
		list(): Promise<Cart[]>;
	};
	// The marketplace storefront catalog (what products belong to). Read-only:
	// the gateway exposes no storefront moderation routes.
	storefronts: {
		list(): Promise<Storefront[]>;
		get(slug: string): Promise<Storefront>;
		products(shopId: string): Promise<Product[]>;
		analytics(shopId: string): Promise<ShopAnalytics>;
		salesSummary(): Promise<ShopSalesSummary[]>;
		/** Партнёрские магазины: кого магазин показывает в бутике и кто — его. Только чтение. */
		partners(shopId: string): Promise<ShopPartners>;
		pickupPoints(shopId: string): Promise<ShopPickupPoints>;
	};
	// Seller-shop moderation, keyed by USER id — the seller pipeline folded
	// into the admin Shops surface. Each method (except list) takes the
	// seller's user id.
	sellers: {
		list(): Promise<SellerShop[]>;
		get(userId: string): Promise<SellerShopDetail>;
		approve(userId: string): Promise<SellerShop>;
		/** final — окончательный отказ: заявка «Отклонён», повторная подача через 90 дней. */
		reject(userId: string, reason?: string, opts?: { final?: boolean }): Promise<SellerShop>;
		block(userId: string): Promise<SellerShop>;
		unblock(userId: string): Promise<SellerShop>;
		assignManager(userId: string, managerUserId: string): Promise<SellerShopDetail>;
		clearManager(userId: string): Promise<SellerShopDetail>;
		// Тимлид магазина. Ключ пути — shopId витрины (или userId продавца без
		// витрины): бэкенд хранит тимлида по shop_id. Эндпоинт возвращает саму
		// привязку, поэтому деталь магазина после вызова перечитывается отдельно.
		assignTeamLead(shopKey: string, leadUserId: string): Promise<void>;
		clearTeamLead(shopKey: string): Promise<void>;
		staff(userId: string): Promise<ShopStaff>;
		updateStaffRole(userId: string, memberId: string, role: StaffAssignableRole): Promise<ShopMember>;
		removeStaffMember(userId: string, memberId: string): Promise<void>;
		// Fetch a seller's onboarding-profile document as raw bytes (private bucket
		// — auth travels in the header, so the bytes come back as a Blob the viewer
		// turns into an object URL). Serves the ProfileDocument list embedded in the
		// shop submission (route /admin/shops/:id/documents/:docId/file).
		profileDocumentFile(userId: string, docId: string): Promise<Blob>;
		// ── Э7: «личное дело» продавца (документы верификации, миграция 044) ──
		// Список документов продавца (GET /admin/sellers/:userId/documents).
		documents(userId: string): Promise<SellerDocumentList>;
		// Приём/отклонение документа модератором с примечанием
		// (POST /admin/documents/:id/review). Возвращает обновлённый документ.
		reviewDocument(
			docId: string,
			input: SellerDocumentReviewInput,
		): Promise<SellerDocument>;
		// Файл документа «личного дела» для модератора, как Blob → object URL
		// (GET /admin/sellers/:userId/documents/:docId/file). Отдельный маршрут от
		// owner-scoped fileUrl из DTO, который админу недоступен.
		documentFile(userId: string, docId: string): Promise<Blob>;
		// Состояние верификации продавца: чек-лист документов (3 состояния),
		// авто-проверка ИНН и итоговый бейдж «Проверенный продавец»
		// (GET /admin/sellers/:userId/verification).
		verification(userId: string): Promise<SellerVerification>;
		// П7: разделы анкеты и решение модератора по разделу
		// (GET /admin/sellers/:userId/sections, POST …/sections/:section/review).
		sections(userId: string): Promise<ProfileSectionState[]>;
		reviewSection(
			userId: string,
			section: ProfileSectionKey,
			input: ProfileSectionReviewInput,
		): Promise<ProfileSectionState[]>;
	};
	// Перевозчики: включение, ключи, зоны курьера, проверка покрытия города.
	deliveryCarriers: {
		list(): Promise<CarrierAdmin[]>;
		setEnabled(id: CarrierId, enabled: boolean): Promise<CarrierAdmin>;
		/** Выключить курьера перевозчика в городе (enabled=false) или вернуть. */
		upsertZone(id: CarrierId, city: string, enabled: boolean): Promise<CarrierAdmin>;
		removeZone(id: CarrierId, zoneId: string): Promise<CarrierAdmin>;
		check(
			id: CarrierId,
			input: { city: string; fromCity?: string; cargo?: "parcel" | "heavy" },
		): Promise<CarrierCheck>;
	};
	settings: {
		getDelivery(): Promise<DeliveryConfig>;
		updateDelivery(patch: DeliveryConfigPatch): Promise<DeliveryConfig>;
		// Правила автомодерации правок: порог роста цены.
		getModeration(): Promise<ModerationSettings>;
		updateModeration(patch: Partial<ModerationSettings>): Promise<ModerationSettings>;
	};
	// Канал SMS: состояние подключения к СМС-Центру, остаток на счёте, журнал
	// отправленных кодов, пробная отправка и догоняющий опрос статусов.
	sms: {
		health(windowHours?: number): Promise<SmsChannelHealth>;
		log(filter?: SmsLogFilter): Promise<SmsLogPage>;
		// dryRun (по умолчанию) — проверка реквизитов без отправки и без списания.
		test(input: { phone: string; text?: string; dryRun?: boolean }): Promise<SmsTestResult>;
		refreshStatuses(limit?: number): Promise<{ updated: number }>;
		/** Журнал обращений к внешним системам и сводка ошибок за сутки. */
		events(filter?: IntegrationEventFilter): Promise<IntegrationEvent[]>;
		eventsSummary(): Promise<IntegrationEventSummary[]>;
	};
	// Раздел «Интеграции»: все внешние сервисы, проверка соединения (только
	// авторизация/статус), расходы на инфраструктуру и счётчик Метрики.
	integrations: {
		overview(): Promise<IntegrationsOverview>;
		check(key: string): Promise<IntegrationCheckResult>;
		costs(): Promise<InfrastructureCosts>;
		updateCosts(patch: InfrastructureCostsPatch): Promise<InfrastructureCosts>;
		setMetrikaCounter(counterId: string | null): Promise<{ counterId: string | null }>;
		// API-ключи магазинов (пакет П5): список и отзыв.
		apiKeys(filter?: { shopId?: string; active?: boolean }): Promise<PartnerApiKey[]>;
		revokeApiKey(id: string): Promise<PartnerApiKey>;
	};
	// Почта: статус провайдера, журнал писем с повтором, тестовое письмо и
	// предпросмотр шаблонов. Чтение — page.integrations, действия —
	// action.integrations.manage.
	email: {
		status(windowHours?: number): Promise<EmailChannelStatus>;
		log(filter?: EmailLogFilter): Promise<EmailLogPage>;
		message(id: string): Promise<EmailMessageDetail>;
		retry(id: string): Promise<EmailSendOutcome>;
		test(to: string): Promise<EmailSendOutcome>;
		resume(): Promise<void>;
		templates(): Promise<EmailTemplateMeta[]>;
		preview(id: string): Promise<EmailTemplatePreview>;
	};
	geo: {
		listCountries(): Promise<GeoCountry[]>;
		updateCountries(countries: GeoCountry[]): Promise<GeoCountry[]>;
		listCurrencies(): Promise<GeoCurrency[]>;
		updateCurrencies(currencies: GeoCurrency[]): Promise<GeoCurrency[]>;
	};
	// Геоаналитика площадки — РЕАЛЬНЫЙ эндпоинт (в отличие от dashboard ниже):
	// ГДЕ находятся продавцы против того, КУДА уходят заказы, и потоки между
	// регионами (GET /admin/analytics/geo-flows), гард page.dashboard.
	analytics: {
		geoFlows(input: { days?: number; limit?: number }): Promise<GeoFlowAnalytics>;
		// fd-analytics-05: блок «Метрика» - Reporting API через шлюз (кеш на час).
		metrika(days: number): Promise<MetrikaReport>;
		ensureMetrikaGoals(): Promise<{ created: string[]; existing: string[] }>;
	};
	// Founder workspace aggregates. MOCK-ONLY for now: the gateway exposes no
	// aggregate/stats routes at all (only per-product /analytics/*), so the http
	// provider answers these with 501 until the endpoints exist.
	dashboard: {
		overview(range: DashboardRange): Promise<DashboardOverview>;
	};
	// Finances is a PROTOTYPE of a model OSMIX does not have: there is no ledger,
	// no commission and no payout table in the backend. Everything here is
	// invented to agree a product direction, not to reflect stored data.
	finances: {
		summary(range: DashboardRange): Promise<FinanceSummary>;
		// Денежные настройки: базовая ставка комиссии, ставки по категориям и
		// индивидуальные по магазинам.
		getSettings(): Promise<FinanceSettings>;
		updateSettings(patch: Partial<FinanceSettings>): Promise<FinanceSettings>;
		/**
		 * Продвинуть обязательство по жизненному циклу — то, что превращает
		 * экран из отчёта в инструмент выплат (правка 27). Шаг назад сервер
		 * отклоняет: иначе отметка об оплате теряет смысл.
		 */
		advanceObligation(id: string, state: ObligationState): Promise<{ id: string; state: ObligationState }>;
		/** Заявки продавцов на выплату — отдельная воронка раздела «Выплаты». */
		payouts(status?: PayoutRequestStatus | "all"): Promise<PayoutRequest[]>;
		/** Страница заявок на выплату (свежие сверху). */
		payoutPage(
			status: PayoutRequestStatus | "all",
			page: number,
			pageSize: number,
		): Promise<ServerPage<PayoutRequest>>;
		/** Расшифровка заявки: отчёт месяца, заказы и владелец магазина. */
		payoutDetails(id: string): Promise<PayoutDetails>;
		decidePayout(
			id: string,
			input: { status: Exclude<PayoutRequestStatus, "new">; comment?: string },
		): Promise<PayoutRequest>;
		/** Сформировать закрывающий документ по обязательству. */
		generateDocuments(id: string): Promise<ObligationDocument[]>;
		/** Реестр платёжных поручений по одобренным заявкам. */
		payoutRegistry(): Promise<PaymentRegistry>;
		/** Файл для 1С (1CClientBankExchange, Windows-1251) или xlsx-реестр. */
		exportPayoutRegistry(format: "1c" | "xlsx"): Promise<DownloadedFile>;
		/** ДДС за период; даты — YYYY-MM-DD по Москве. */
		cashFlow(from: string, to: string): Promise<CashFlowReport>;
		exportCashFlow(from: string, to: string, format: "xlsx" | "csv"): Promise<DownloadedFile>;
		/** Бухгалтерия: продавцы с оборотом — для фильтра. */
		accountingShops(): Promise<AccountingShop[]>;
		accountingSales(query: AccountingQuery): Promise<AccountingSalesSummary>;
		accountingReturns(query: AccountingQuery): Promise<AccountingReturnsSummary>;
		agentReports(query: AccountingQuery): Promise<AgentReportSummary[]>;
		commissionActs(query: AccountingQuery): Promise<CommissionActSummary[]>;
		settlements(query: AccountingQuery): Promise<SettlementsReport>;
		accountingJournal(): Promise<AccountingJournalEntry[]>;
		/** Файл выгрузки: xlsx, csv или печатная форма отчёта агента (html). */
		exportAccounting(
			dataset: AccountingDataset,
			query: AccountingQuery,
			format: "xlsx" | "csv" | "html" | "xml",
		): Promise<DownloadedFile>;
	};
	// Раздел «Комиссии»: ставки по категориям, магазинам и парам магазин×категория.
	commissions: {
		overview(days?: number): Promise<CommissionOverview>;
		shops(days?: number): Promise<CommissionShopList>;
		categoryShops(categoryId: string, days?: number): Promise<CommissionCategoryShops>;
		history(filter?: { shopId?: string; categoryId?: string; limit?: number }): Promise<CommissionHistoryEntry[]>;
		setBase(rate: number): Promise<{ baseRate: number }>;
		setRate(change: CommissionRateChange): Promise<{ changed: number }>;
		bulk(input: CommissionBulkInput): Promise<{ changed: number }>;
	};
	// «Скидка OSMIX»: промокоды площадки (gateway-бэкенд, миграции 056–057).
	// Скидку в рублях считает корзина; здесь — жизненный цикл кода и лимиты
	// использования (общий и на аккаунт), гард action.finances.manage.
	promos: {
		list(): Promise<PromoCode[]>;
		get(code: string): Promise<PromoCode>;
		stats(code: string, period?: PromoPeriod): Promise<PromoStats>;
		exportStats(code: string, period: PromoPeriod, format: "xlsx" | "csv"): Promise<DownloadedFile>;
		create(input: PromoCodeInput): Promise<PromoCode>;
		update(code: string, patch: Partial<PromoCodeInput> & { archived?: boolean }): Promise<PromoCode>;
		remove(code: string): Promise<void>;
		check(code: string, audience: "buyer" | "seller"): Promise<PromoCheckResult>;
		settings(): Promise<PartnerSettings>;
		updateSettings(input: PartnerSettings): Promise<PartnerSettings>;
		sellerBinding(userId: string): Promise<SellerPartnerPromo>;
		bindSeller(userId: string, promo: string | null, reason?: string): Promise<SellerPartnerPromo>;
		/** fd-promo-12: персональные предложения. */
		personalOffers(): Promise<PersonalOffersOverview>;
		updatePersonalOffers(input: PersonalOffersSettings): Promise<PersonalOffersSettings>;
		runPersonalOffers(): Promise<PersonalOffersRunResult>;
	};
	// Партнёры — владельцы промокодов (миграция 130). Выплат нет: «начислено»
	// считается по условию вознаграждения каждого кода.
	partners: {
		list(search?: string): Promise<Partner[]>;
		detail(id: string, period?: PromoPeriod): Promise<PartnerDetail>;
		create(input: PartnerInput): Promise<Partner>;
		update(id: string, patch: PartnerPatch): Promise<Partner>;
		remove(id: string): Promise<void>;
	};
	// Seller onboarding kanban. MOCK-ONLY: the gateway models this as
	// `seller_profiles.onboarding_status` + `shop_moderation` with no board, no
	// assignee ownership and no automation rules.
	applications: {
		board(): Promise<ApplicationBoard>;
		/** Move a card to another column. */
		/** reason обязателен для «Отклонён»: продавец увидит причину. */
		move(id: string, status: ApplicationStatus, reason?: string): Promise<SellerApplication>;
		/** Set or clear the responsible manager. */
		assign(id: string, assigneeId: string | null): Promise<SellerApplication>;
		/**
		 * Send an application to «На доработке» with a written remark (+ optional
		 * deadline / section). The seller resubmits it back to moderation.
		 */
		rework(id: string, input: ApplicationReworkInput): Promise<ApplicationReworkResult>;
		/**
		 * Завести кабинет продавца прямо из заявки: второй вход площадки, когда
		 * бизнес оставил телефон, а регистрацию за него делает менеджер. Пароль не
		 * высылается — продавцу уходит одноразовая ссылка.
		 */
		createAccount(id: string, input: ApplicationAccountInput): Promise<ApplicationAccountResult>;
	};
	// Analytics reports (gateway-backed). `generate` synthesises an HTML document
	// server-side (AI variants take 5–15s); `getHtml` re-fetches a stored report's
	// HTML as text, carrying the bearer token the browser cannot attach to an
	// <iframe> src — so the viewer renders it via srcDoc instead.
	reports: {
		catalog(): Promise<ReportCatalogEntry[]>;
		/** Модель ИИ для отчётов; пустая строка — модель из конфигурации стенда. */
		getModel(): Promise<string>;
		setModel(model: string): Promise<string>;
		generate(type: string, days?: number): Promise<GeneratedReport>;
		history(): Promise<ReportHistoryEntry[]>;
		getHtml(id: string): Promise<string>;
	};
	// Центр помощи (Э5, fd-support-01), гейтвей-бэкенд, миграция 045. FAQ —
	// «вопрос/ответ» по категориям (админка управляет всеми, включая черновики).
	// Обращения — переписка пользователя с поддержкой: ответ поддержки и смена
	// статуса возвращают обновлённую деталь тикета целиком.
	support: {
		faq(): Promise<FaqArticle[]>;
		createFaq(input: CreateFaqArticleRequest): Promise<FaqArticle>;
		updateFaq(id: string, input: UpdateFaqArticleRequest): Promise<FaqArticle>;
		deleteFaq(id: string): Promise<void>;
		tickets(status?: SupportTicketStatus): Promise<SupportTicket[]>;
		ticket(id: string): Promise<SupportTicketDetail>;
		reply(id: string, input: TicketMessageRequest): Promise<SupportTicketDetail>;
		setStatus(id: string, status: SupportTicketStatus): Promise<SupportTicketDetail>;
		// Файл к ответу поддержки: загружается отдельно, в ответ кладётся ссылка.
		uploadAttachment(file: File): Promise<SupportAttachment>;
	};
	// CRM заявок (04.10.2026): карточка заявки, контакты, касания, задачи
	// «следующий шаг», файлы, ручное заведение, загрузка и выгрузка базы.
	// Стадии/ответственный/кабинет — в `applications`.
	applicationCrm: {
		card(id: string): Promise<ApplicationCard>;
		create(input: CrmLeadCreate): Promise<ApplicationCard>;
		duplicates(query: { phone?: string; email?: string; inn?: string; excludeId?: string }): Promise<CrmDuplicate[]>;
		update(id: string, patch: CrmLeadPatch): Promise<ApplicationCard>;
		fillCompany(id: string): Promise<ApplicationCard>;
		addContact(id: string, input: CrmContactInput): Promise<CrmContact>;
		updateContact(id: string, contactId: string, patch: CrmContactPatch): Promise<CrmContact>;
		deleteContact(id: string, contactId: string): Promise<void>;
		addActivity(id: string, input: CrmActivityInput): Promise<ApplicationCard>;
		updateActivity(id: string, activityId: string, patch: CrmActivityPatch): Promise<CrmActivity>;
		deleteActivity(id: string, activityId: string): Promise<void>;
		addTask(id: string, input: CrmTaskInput): Promise<CrmTask>;
		updateTask(id: string, taskId: string, patch: CrmTaskPatch): Promise<CrmTask>;
		deleteTask(id: string, taskId: string): Promise<void>;
		tasks(scope: CrmTaskScope, due: CrmTaskDueFilter): Promise<CrmTaskWithLead[]>;
		uploadFile(id: string, file: File, category?: CrmFileCategory): Promise<CrmFile>;
		/** Байты файла (с токеном консоли) — для превью и скачивания. */
		fileBlob(id: string, fileId: string): Promise<Blob>;
		updateFile(id: string, fileId: string, patch: CrmFilePatch): Promise<CrmFile>;
		deleteFile(id: string, fileId: string): Promise<void>;
		importLeads(input: CrmLeadImport): Promise<CrmLeadImportResult>;
		exportLeads(): Promise<{ blob: Blob; filename: string; rows: number }>;
	};
	// ИИ-загрузка каталога (04.10.2026): материалы → черновики магазина и
	// товаров (OpenRouter) → проверка → загрузка через сервис каталога.
	catalogImport: {
		jobs(query?: { applicationId?: string; shopId?: string }): Promise<CatalogImportJob[]>;
		create(input: CatalogImportJobCreate): Promise<CatalogImportJob>;
		job(id: string): Promise<CatalogImportJobDetail>;
		update(id: string, patch: CatalogImportJobPatch): Promise<CatalogImportJob>;
		cancel(id: string): Promise<void>;
		/**
		 * Один файл за вызов; архив распаковывается — вернутся все файлы из него.
		 * `path` — путь внутри перетащенной папки («Платья/Ривьера/1.jpg»).
		 */
		uploadFile(id: string, file: File, path?: string): Promise<CatalogImportFile[]>;
		addText(id: string, input: CatalogImportTextInput): Promise<CatalogImportFile>;
		addFromApplication(id: string, input: CatalogImportFromApplication): Promise<CatalogImportFile[]>;
		deleteFile(id: string, fileId: string): Promise<void>;
		fileBlob(id: string, fileId: string): Promise<Blob>;
		parse(id: string): Promise<CatalogImportJob>;
		updateItem(id: string, itemId: string, patch: CatalogImportItemPatch): Promise<CatalogImportItem>;
		bulkUpdate(id: string, input: CatalogImportBulkPatch): Promise<{ updated: number }>;
		regenerateItem(id: string, itemId: string, input: CatalogImportRegenerate): Promise<CatalogImportItem>;
		commit(id: string, input?: CatalogImportCommit): Promise<CatalogImportJob>;
		template(): Promise<{ blob: Blob; filename: string }>;
		models(): Promise<CatalogImportModels>;
	};
	// Журнал действий консоли: кто, что и когда сделал, включая отказы доступа.
	audit: {
		list(filter?: AuditFilter): Promise<AuditPage>;
		facets(): Promise<AuditFacets>;
		forEntity(kind: string, id: string): Promise<AuditEntry[]>;
	};
};

/** API-ключ магазина для публичного API /partner/v1 (пакет П5). Секрета в ответе нет. */
export type PartnerApiKey = {
	id: string;
	shopId: string;
	shopName: string;
	name: string;
	prefix: string;
	scopes: string[];
	createdAt: string;
	lastUsedAt?: string;
	revokedAt?: string;
};
